import { ChangeEvent, FormEvent, ReactNode, Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Building2,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Download,
  ExternalLink,
  FileUp,
  FileSpreadsheet,
  KeyRound,
  Library,
  Lock,
  Mail,
  MessageSquare,
  PackageCheck,
  PenLine,
  PlugZap,
  ReceiptText,
  Search,
  Send,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Truck,
  UploadCloud,
  UserPlus,
  Users
} from "lucide-react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import {
  alaCarteServices,
  businessCopy,
  comparisonRows,
  inquiryChoices,
  noPromises,
  operations,
  publishingPackages,
  servicePillars
} from "./data/catalog";
import type { Author, Book, Series } from "./data/catalog";
import { apiGet, apiPatch, apiPost, apiUpload, AuthProvider, authStart, PortalUser } from "./lib/api";
import { inquirySchema } from "./lib/schemas";
import { CastlehaleMark } from "./components/CastlehaleMark";

const BackgroundBall = lazy(() => import("./components/BackgroundBall"));

type CatalogAuthor = Author & {
  id?: number;
  status?: string;
  publicContactNote?: string;
  assets?: Record<string, unknown>[];
};

type CatalogBook = Book & {
  id?: number;
  authorId?: number;
  authorName?: string;
  publicationStatus?: string;
  coverImage?: string;
  editions?: Record<string, unknown>[];
  isbns?: Record<string, unknown>[];
  assets?: Record<string, unknown>[];
};

type CatalogSeries = Series & {
  id?: number;
  authorId?: number;
  status?: string;
};

type CatalogState = {
  authors: CatalogAuthor[];
  books: CatalogBook[];
  series: CatalogSeries[];
  source: "d1";
  loading: boolean;
  error?: string;
};

function usePublicCatalog(): CatalogState {
  const query = useQuery({
    queryKey: ["publicCatalog"],
    queryFn: async () => normalizeCatalog(await apiGet<Record<string, unknown>>("/api/public/catalog"))
  });
  return {
    authors: query.data?.authors ?? [],
    books: query.data?.books ?? [],
    series: query.data?.series ?? [],
    source: "d1",
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : query.error ? "Unable to load catalog" : undefined
  };
}

function normalizeCatalog(result: Record<string, unknown>): Omit<CatalogState, "loading" | "source"> {
  const authors = arrayOfRecords(result.authors).map((row): CatalogAuthor => ({
    id: numberFrom(row.id),
    slug: stringField(row, "slug"),
    name: stringField(row, "name"),
    role: stringField(row, "role") || "Castlehale author",
    photo: stringField(row, "photo") || "/assets/castlehale-hero.png",
    shortBio: stringField(row, "shortBio") || stringField(row, "short_bio"),
    longBio: stringField(row, "longBio") || stringField(row, "long_bio"),
    interviewTopics: arrayOfStrings(row.interviewTopics),
    pressKit: `/authors/${stringField(row, "slug")}`,
    status: stringField(row, "status"),
    publicContactNote: stringField(row, "publicContactNote"),
    assets: arrayOfRecords(row.assets)
  })).filter((author) => author.slug && author.name);

  const series = arrayOfRecords(result.series).map((row): CatalogSeries => ({
    id: numberFrom(row.id),
    slug: stringField(row, "slug"),
    title: stringField(row, "title"),
    authorSlug: stringField(row, "authorSlug") || stringField(row, "author_slug"),
    authorId: numberFrom(row.authorId ?? row.author_id),
    description: stringField(row, "description"),
    promise: stringField(row, "promise") || stringField(row, "reader_promise"),
    status: stringField(row, "status")
  })).filter((item) => item.slug && item.title);

  const books = arrayOfRecords(result.books).map((row, index): CatalogBook => {
    const editions = arrayOfRecords(row.editions);
    const apiFormats = arrayOfStrings(row.formats) as Book["formats"];
    const apiListPrices = isRecord(row.listPrices) ? Object.fromEntries(Object.entries(row.listPrices).map(([format, price]) => [format, String(price)])) : {};
    const listPrices = Object.fromEntries(
      editions
        .map((edition) => [stringField(edition, "format"), numberField(edition, "list_price") ? money(numberField(edition, "list_price")) : ""])
        .filter(([format, price]) => format && price)
    );
    const firstEdition = editions[0] ?? {};
    return {
      id: numberFrom(row.id),
      authorId: numberFrom(row.authorId ?? row.author_id),
      authorName: stringField(row, "authorName"),
      slug: stringField(row, "slug"),
      title: stringField(row, "title"),
      subtitle: stringField(row, "subtitle"),
      hook: stringField(row, "hook") || stringField(row, "short_hook"),
      promise: stringField(row, "promise") || stringField(row, "reader_promise"),
      description: stringField(row, "description"),
      authorSlug: stringField(row, "authorSlug") || stringField(row, "author_slug"),
      seriesSlug: stringField(row, "seriesSlug") || stringField(row, "series_slug"),
      seriesOrder: numberFrom(row.seriesOrder ?? row.series_order) || index + 1,
      coverTone: stringField(row, "coverTone") || "charcoal",
      coverImage: stringField(row, "coverImage"),
      formats: apiFormats.length
        ? apiFormats
        : (editions.map((edition) => stringField(edition, "format")).filter(Boolean) as Book["formats"]).length
        ? (editions.map((edition) => stringField(edition, "format")).filter(Boolean) as Book["formats"])
        : ["Paperback"],
      isbn: isRecord(row.isbn) ? Object.fromEntries(Object.entries(row.isbn).map(([key, value]) => [key, String(value)])) : {},
      asin: stringField(row, "asin"),
      ingramTitleId: stringField(row, "ingramTitleId"),
      categories: arrayOfStrings(row.categories),
      keywords: arrayOfStrings(row.keywords),
      trimSize: stringField(firstEdition, "trim_size"),
      pageCount: numberFrom(firstEdition.page_count),
      publicationDate: stringField(row, "publicationDate") || stringField(row, "publication_date"),
      listPrices: Object.keys(apiListPrices).length ? apiListPrices : listPrices,
      buyLinks: arrayOfRecords(row.buyLinks).map((link) => ({
        label: stringField(link, "label"),
        href: stringField(link, "href") || stringField(link, "url"),
        channel: stringField(link, "channel")
      })),
      publicationStatus: stringField(row, "publicationStatus"),
      editions,
      isbns: arrayOfRecords(row.isbns),
      assets: arrayOfRecords(row.assets)
    };
  }).filter((book) => book.slug && book.title);

  return {
    authors,
    books,
    series
  };
}

function getCatalogAuthor(catalog: Pick<CatalogState, "authors">, slug: string) {
  return catalog.authors.find((author) => author.slug === slug);
}

function getCatalogBook(catalog: Pick<CatalogState, "books">, slug: string) {
  return catalog.books.find((book) => book.slug === slug);
}

function getCatalogSeries(catalog: Pick<CatalogState, "series">, slug: string) {
  return catalog.series.find((item) => item.slug === slug);
}

function catalogBooksByAuthor(catalog: Pick<CatalogState, "books">, slug: string) {
  return catalog.books.filter((book) => book.authorSlug === slug).sort((a, b) => a.seriesOrder - b.seriesOrder);
}

function catalogBooksBySeries(catalog: Pick<CatalogState, "books">, slug: string) {
  return catalog.books.filter((book) => book.seriesSlug === slug).sort((a, b) => a.seriesOrder - b.seriesOrder);
}

function setSeo(title: string, description: string, jsonLd?: object) {
  document.title = `${title} | Castlehale Press`;
  const descriptionTag =
    document.querySelector<HTMLMetaElement>("meta[name='description']") ??
    document.head.appendChild(document.createElement("meta"));
  descriptionTag.name = "description";
  descriptionTag.content = description;

  const ogTitle =
    document.querySelector<HTMLMetaElement>("meta[property='og:title']") ??
    document.head.appendChild(document.createElement("meta"));
  ogTitle.setAttribute("property", "og:title");
  ogTitle.content = `${title} | Castlehale Press`;

  const ogDescription =
    document.querySelector<HTMLMetaElement>("meta[property='og:description']") ??
    document.head.appendChild(document.createElement("meta"));
  ogDescription.setAttribute("property", "og:description");
  ogDescription.content = description;

  const existing = document.getElementById("castlehale-jsonld");
  if (existing) existing.remove();
  if (jsonLd) {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "castlehale-jsonld";
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }
}

function useSeo(title: string, description: string, jsonLd?: object) {
  useEffect(() => setSeo(title, description, jsonLd), [title, description, jsonLd]);
}

function App() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/books" element={<BooksPage />} />
        <Route path="/books/:slug" element={<BookPage />} />
        <Route path="/authors" element={<AuthorsPage />} />
        <Route path="/authors/:slug" element={<AuthorPage />} />
        <Route path="/series" element={<SeriesPage />} />
        <Route path="/series/:slug" element={<SeriesDetailPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/libraries" element={<LibrariesPage />} />
        <Route path="/press" element={<PressPage />} />
        <Route path="/rights" element={<RightsPage />} />
        <Route path="/submissions" element={<SubmissionsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/publish-with-us" element={<PublishWithUsPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/author-services" element={<AuthorServicesPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin" element={<PortalPage kind="admin" />} />
        <Route path="/author" element={<PortalPage kind="author" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function Layout({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  useEffect(() => {
    setOpen(false);
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>(".content-scroll")?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [location.pathname]);

  const nav = [
    ["/books", "Books"],
    ["/authors", "Authors"],
    ["/pricing", "Pricing"],
    ["/libraries", "Libraries"],
    ["/rights", "Rights"],
    ["/press", "Press"],
    ["/login", "Login"]
  ];

  return (
    <>
      <Suspense fallback={null}>
        <BackgroundBall className="background-ball" />
      </Suspense>
      <div className="background-vignette" />
      <header className="site-header">
        <Link className="brand" to="/" aria-label="Castlehale Press home">
          <span className="brand-mark">
            <CastlehaleMark />
          </span>
          <span>
            <strong>Castlehale Press</strong>
            <small>Independent publisher</small>
          </span>
        </Link>
        <button className="menu-button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
          Menu
        </button>
        <nav className={open ? "nav open" : "nav"} aria-label="Primary navigation">
          {nav.map(([href, label]) => (
            <NavLink key={href} to={href}>
              {label}
            </NavLink>
          ))}
          <Link className="nav-cta" to="/publish-with-us">
            Publish
          </Link>
        </nav>
      </header>
      <div className="content-scroll">
        <main className={isHome ? "home-main" : ""}>{children}</main>
      </div>
      <footer className="site-footer">
        <nav className="footer-routes" aria-label="Footer navigation">
          <Link className="footer-route" to="/books">
            <BookOpen size={24} />
            <span>
              <strong>Books</strong>
              <small>Browse the catalog</small>
            </span>
          </Link>
          <Link className="footer-route" to="/authors">
            <Users size={24} />
            <span>
              <strong>Authors</strong>
              <small>Bios and books</small>
            </span>
          </Link>
          <Link className="footer-route" to="/publish-with-us">
            <PenLine size={24} />
            <span>
              <strong>Publish</strong>
              <small>Services by contract</small>
            </span>
          </Link>
          <Link className="footer-route" to="/libraries">
            <Library size={24} />
            <span>
              <strong>Libraries</strong>
              <small>Acquisitions support</small>
            </span>
          </Link>
        </nav>
      </footer>
    </>
  );
}

function Section({
  eyebrow,
  title,
  intro,
  children,
  className = ""
}: {
  eyebrow?: string;
  title: string;
  intro?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`section ${className}`}>
      <div className="section-heading">
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h2>{title}</h2>
        {intro && <p>{intro}</p>}
      </div>
      {children}
    </section>
  );
}

function HomePage() {
  const catalog = usePublicCatalog();
  const authors = catalog.authors;
  const books = catalog.books;
  const [focusedAuthor, setFocusedAuthor] = useState(0);
  const activeAuthor = authors[focusedAuthor] ?? authors[0];
  const activeAuthorBooks = activeAuthor ? catalogBooksByAuthor(catalog, activeAuthor.slug).slice(0, 3) : [];
  const topBooks = books.slice(0, 10);
  const featuredBook = topBooks[0];
  const featuredBookAuthor = featuredBook ? getCatalogAuthor(catalog, featuredBook.authorSlug) : undefined;

  useSeo(
    "Independent Publisher",
    "Castlehale Press publishes books, supports writers, and gives readers, libraries, and rights buyers a clear path to the right title.",
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Castlehale Press",
      url: "https://castlehalepress.com",
      sameAs: []
    }
  );

  return (
    <div className="home-redesign">
      <section className="home-hero-2026">
        <div className="home-hero-content">
          <p className="eyebrow">Independent publishing house</p>
          <h1>Castlehale Press</h1>
          <p>
            A publisher for books that need a clear public home: catalog pages, library ordering, rights routing, direct sales, and contracted publishing services.
          </p>
          <div className="button-row">
            <Link className="button primary" to="/books">
              Browse books <ArrowRight size={17} />
            </Link>
            <Link className="button" to="/publish-with-us">
              Publish with us
            </Link>
          </div>
        </div>

        {featuredBook && (
          <Link className="home-feature-line" to={`/books/${featuredBook.slug}`}>
            <span>Featured</span>
            <strong>{featuredBook.title}</strong>
            <small>{featuredBookAuthor?.name || featuredBook.authorName}</small>
            <ArrowRight size={16} />
          </Link>
        )}
      </section>

      <section className="home-catalog-band" aria-label="Castlehale catalog">
        <div className="home-band-heading">
          <div>
            <p className="eyebrow">Spotlight</p>
            <h2>Featured voice</h2>
          </div>
          <Link to="/authors">Full roster <ChevronRight size={15} /></Link>
        </div>
        {catalog.loading && <p className="note">Loading catalog...</p>}
        {catalog.error && <p className="form-status error">{catalog.error}</p>}
        {authors.length > 1 && (
          <div className="home-author-tabs" role="tablist" aria-label="Author carousel">
            {authors.map((author, index) => (
              <button
                className={index === focusedAuthor ? "active" : ""}
                key={author.slug}
                onClick={() => setFocusedAuthor(index)}
                onFocus={() => setFocusedAuthor(index)}
                role="tab"
                type="button"
              >
                <span>{author.name}</span>
              </button>
            ))}
          </div>
        )}
        {activeAuthor && (
          <div className="home-author-feature">
            <div className="home-author-copy">
              <p className="eyebrow">Castlehale catalog</p>
              <h3>{activeAuthor.name}</h3>
              <p>{activeAuthor.shortBio}</p>
              <Link className="text-action" to={`/authors/${activeAuthor.slug}`}>
                Profile <ArrowRight size={15} />
              </Link>
            </div>
            <div className="home-author-books">
              {activeAuthorBooks.length > 0 ? activeAuthorBooks.map((book) => (
                <Link to={`/books/${book.slug}`} key={book.slug}>
                  {book.coverImage && (
                    <span className="home-work-cover">
                      <BookCover title={book.title} tone={book.coverTone} order={book.seriesOrder} image={book.coverImage} />
                    </span>
                  )}
                  <strong>{book.title}</strong>
                  <span>{book.hook || book.subtitle}</span>
                </Link>
              )) : <p className="note">Books assigned to this profile will appear here when published in the catalog.</p>}
            </div>
          </div>
        )}
      </section>

      <section className="home-books-band" aria-label="Castlehale books">
        <div className="home-band-heading">
          <div>
            <p className="eyebrow">Books</p>
            <h2>Current and forthcoming titles</h2>
          </div>
          <Link to="/books">Open catalog <ChevronRight size={15} /></Link>
        </div>
        {!catalog.loading && !catalog.error && topBooks.length === 0 && <p className="note">No public books are listed yet.</p>}
        <div className="home-title-list">
          {topBooks.slice(0, 6).map((book) => (
            <Link to={`/books/${book.slug}`} key={book.slug}>
              <div className="home-title-cover">
                <BookCover title={book.title} tone={book.coverTone} order={book.seriesOrder} image={book.coverImage} />
              </div>
              <div>
                <strong>{book.title}</strong>
                <span>{getCatalogAuthor(catalog, book.authorSlug)?.name || book.authorName}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function BooksPage() {
  const catalog = usePublicCatalog();
  useSeo("Books", "Browse Castlehale Press books, editions, series, direct-sale links, and library notes.");
  return (
    <>
      <PageHero eyebrow="Books" title="Books from Castlehale Press." copy="Browse current and forthcoming titles, meet the authors, and find the right path for readers, libraries, reviewers, and rights buyers." />
      <Section title="Catalog" className="book-catalog-section">
        {catalog.loading && <p className="note">Loading catalog...</p>}
        {catalog.error && <p className="form-status error">{catalog.error}</p>}
        {!catalog.loading && !catalog.error && catalog.books.length === 0 && <p className="note">No public books are listed yet.</p>}
        <div className="book-list">
          {catalog.books.map((book) => {
            const author = getCatalogAuthor(catalog, book.authorSlug);
            return (
              <article className="book-list-item" key={book.slug}>
                <Link to={`/books/${book.slug}`} className="book-list-cover">
                  <BookCover title={book.title} tone={book.coverTone} order={book.seriesOrder} image={book.coverImage} />
                </Link>
                <div className="book-list-copy">
                  <p className="eyebrow">{author?.name || book.authorName}</p>
                  <h2>
                    <Link to={`/books/${book.slug}`}>{book.title}</Link>
                  </h2>
                  <p>{book.hook || book.description}</p>
                  <div className="book-list-actions">
                    <Link className="text-action" to={`/books/${book.slug}`}>
                      Read more <ArrowRight size={15} />
                    </Link>
                    <Link className="text-action" to="/shop">
                      Buy or request a copy <ArrowRight size={15} />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </Section>
    </>
  );
}

function BookPage() {
  const { slug = "" } = useParams();
  const catalog = usePublicCatalog();
  const book = getCatalogBook(catalog, slug);
  const author = book ? getCatalogAuthor(catalog, book.authorSlug) : undefined;
  const related = book ? catalogBooksBySeries(catalog, book.seriesSlug).filter((item) => item.slug !== book.slug).slice(0, 3) : [];
  const jsonLd = useMemo(
    () =>
      book && author
        ? {
            "@context": "https://schema.org",
            "@type": "Book",
            name: book.title,
            author: { "@type": "Person", name: author.name },
            datePublished: book.publicationDate,
            bookFormat: book.formats
          }
        : undefined,
    [book, author]
  );
  useSeo(book?.title ?? "Book", book?.hook ?? "Castlehale Press book page.", jsonLd);
  if (catalog.loading) return <PageHero eyebrow="Book" title="Loading title..." copy="Castlehale is loading the public catalog." />;
  if (catalog.error) return <PageHero eyebrow="Catalog error" title="Book catalog unavailable." copy={catalog.error} />;
  if (!book || !author) return <NotFound title="Book not found" />;

  return (
    <article className="book-sales-page">
      <div className="book-sales-hero">
        <div className="book-sales-cover">
          <BookCover title={book.title} tone={book.coverTone} order={book.seriesOrder} image={book.coverImage} large />
        </div>
        <div className="book-sales-copy">
          <Link className="eyebrow" to={`/authors/${author.slug}`}>{author.name}</Link>
          <h1>{book.title}</h1>
          <p className="lede">{book.hook}</p>
          <p>{book.description}</p>
          <div className="book-buy-row">
            {book.buyLinks.length > 0 ? (
              book.buyLinks.slice(0, 3).map((link, index) => (
                <a className={index === 0 ? "button primary" : "button"} href={link.href} key={`${link.label}-${link.href}`}>
                  {link.label} <ArrowRight size={16} />
                </a>
              ))
            ) : (
              <Link className="button primary" to="/shop">
                Ask about availability <ArrowRight size={16} />
              </Link>
            )}
            <Link className="button" to="/libraries">
              Library inquiry
            </Link>
          </div>
        </div>
      </div>

      <div className="book-sales-layout">
        <section className="reader-panel">
          <p className="eyebrow">Why read it</p>
          <h2>{book.promise}</h2>
          <Link className="text-action" to={`/authors/${author.slug}`}>
            More from {author.name} <ArrowRight size={15} />
          </Link>
        </section>

        <aside className="reader-panel purchase-panel">
          <p className="eyebrow">Available formats</p>
          {Object.entries(book.listPrices).length > 0 ? (
            Object.entries(book.listPrices).map(([format, price]) => (
              <div className="format-line" key={format}>
                <span>{format}</span>
                <strong>{price}</strong>
              </div>
            ))
          ) : (
            <p className="note">Formats and pricing will appear as the title opens for sale.</p>
          )}
          <Link className="text-action" to="/libraries">
            Library or institutional request <ArrowRight size={15} />
          </Link>
        </aside>
      </div>

      <section className="author-book-band">
        <img src={author.photo} alt="" />
        <div>
          <p className="eyebrow">About the author</p>
          <h2>{author.name}</h2>
          <p>{author.shortBio}</p>
          <Link className="text-action" to={`/authors/${author.slug}`}>
            Author page <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {related.length > 0 && (
        <Section title="More in this line" className="related-book-section">
          <div className="book-strip">
            {related.map((item) => (
              <BookCard key={item.slug} book={item} compact />
            ))}
          </div>
        </Section>
      )}
    </article>
  );
}

function AuthorsPage() {
  const catalog = usePublicCatalog();
  useSeo("Authors", "Meet Castlehale Press authors through public pages, books, biographies, and press materials.");
  return (
    <>
      <PageHero eyebrow="Authors" title="Castlehale authors." copy="Author pages collect books, biographies, press materials, and publisher contact paths in one place." />
      <Section title="Castlehale author roster">
        {catalog.loading && <p className="note">Loading authors...</p>}
        {catalog.error && <p className="form-status error">{catalog.error}</p>}
        {!catalog.loading && !catalog.error && catalog.authors.length === 0 && <p className="note">No public authors are listed yet.</p>}
        <div className="author-grid">
          {catalog.authors.map((author) => (
            <AuthorCard key={author.slug} author={author} />
          ))}
        </div>
      </Section>
    </>
  );
}

function AuthorPage() {
  const { slug = "" } = useParams();
  const catalog = usePublicCatalog();
  const author = getCatalogAuthor(catalog, slug);
  const authoredBooks = author ? catalogBooksByAuthor(catalog, author.slug) : [];
  const authorSeries = author ? catalog.series.filter((item) => item.authorSlug === author.slug) : [];
  const jsonLd = useMemo(
    () =>
      author
        ? {
            "@context": "https://schema.org",
            "@type": "Person",
            name: author.name,
            worksFor: { "@type": "Organization", name: "Castlehale Press" }
          }
        : undefined,
    [author]
  );
  useSeo(author?.name ?? "Author", author?.shortBio ?? "Castlehale Press author page.", jsonLd);
  if (catalog.loading) return <PageHero eyebrow="Authors" title="Loading author..." copy="Castlehale is loading the public catalog." />;
  if (catalog.error) return <PageHero eyebrow="Catalog error" title="Author catalog unavailable." copy={catalog.error} />;
  if (!author) return <NotFound title="Author not found" />;

  return (
    <article className="author-profile-page">
      <section className="author-profile-hero">
        <div className="author-profile-copy">
          <p className="eyebrow">{author.role}</p>
          <h1>{author.name}</h1>
          <p className="lede">{author.shortBio}</p>
          <p>{author.longBio}</p>
          <div className="button-row">
            <Link className="button primary" to="/books">
              Browse books <ArrowRight size={17} />
            </Link>
            <Link className="button" to="/libraries">
              Library request
            </Link>
            <Link className="button" to="/rights">
              Rights inquiry
            </Link>
          </div>
        </div>
        <aside className="author-profile-facts">
          <div>
            <small>Books</small>
            <strong>{authoredBooks.length}</strong>
          </div>
          <div>
            <small>Series</small>
            <strong>{authorSeries.length}</strong>
          </div>
          <div>
            <small>Publisher</small>
            <strong>Castlehale Press</strong>
          </div>
        </aside>
      </section>

      <section className="author-profile-books">
        <div className="home-band-heading">
          <div>
            <p className="eyebrow">Books</p>
            <h2>Titles by {author.name}</h2>
          </div>
        </div>
        <div className="author-book-list">
          {authoredBooks.map((book) => (
            <Link to={`/books/${book.slug}`} key={book.slug}>
              <BookCover title={book.title} tone={book.coverTone} order={book.seriesOrder} image={book.coverImage} />
              <span>
                <strong>{book.title}</strong>
                <small>{book.hook}</small>
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section className="author-profile-footer">
        <div>
          <p className="eyebrow">Series</p>
          <h2>Reading paths</h2>
          <div>
            {authorSeries.map((item) => (
              <Link className="callout-link" to={`/series/${item.slug}`} key={item.slug}>
                {item.title}
                <ChevronRight size={18} />
              </Link>
            ))}
            {authorSeries.length === 0 && <p className="note">Series pages will appear here as books are added to the catalog.</p>}
          </div>
        </div>
        <div>
          <p className="eyebrow">Publisher contact</p>
          <h2>Requests route through Castlehale.</h2>
          <p className="note">Library, rights, press, and reader inquiries go through the publisher so the author page stays public, clean, and current.</p>
          <div className="button-row">
            <Link className="button" to="/contact">Contact paths</Link>
            <Link className="button" to="/press">Press materials</Link>
          </div>
        </div>
      </section>
    </article>
  );
}

function SeriesPage() {
  const catalog = usePublicCatalog();
  useSeo("Series", "Browse Castlehale Press series pages, reading order, and direct editions.");
  return (
    <>
      <PageHero eyebrow="Reading order" title="Books in order." copy="Follow related Castlehale titles by reading order, edition, and signed-copy availability." />
      <Section title="Active series">
        {catalog.loading && <p className="note">Loading series...</p>}
        {catalog.error && <p className="form-status error">{catalog.error}</p>}
        {!catalog.loading && !catalog.error && catalog.series.length === 0 && <p className="note">No public series are listed yet.</p>}
        <div className="series-grid">
          {catalog.series.map((item) => (
            <Link className="series-card" to={`/series/${item.slug}`} key={item.slug}>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <span>View series <ArrowRight size={16} /></span>
            </Link>
          ))}
        </div>
      </Section>
    </>
  );
}

function SeriesDetailPage() {
  const { slug = "" } = useParams();
  const catalog = usePublicCatalog();
  const item = getCatalogSeries(catalog, slug);
  const seriesBooks = item ? catalogBooksBySeries(catalog, item.slug) : [];
  useSeo(item?.title ?? "Series", item?.description ?? "Castlehale Press series page.");
  if (catalog.loading) return <PageHero eyebrow="Series" title="Loading series..." copy="Castlehale is loading the public catalog." />;
  if (catalog.error) return <PageHero eyebrow="Catalog error" title="Series catalog unavailable." copy={catalog.error} />;
  if (!item) return <NotFound title="Series not found" />;
  return (
    <>
      <PageHero eyebrow="Series" title={item.title} copy={item.description} />
      <Section title="Series promise" intro={item.promise}>
        <div className="book-strip">
          {seriesBooks.map((book) => (
            <BookCard key={book.slug} book={book} compact />
          ))}
        </div>
      </Section>
    </>
  );
}

function ShopPage() {
  const catalog = usePublicCatalog();
  const location = useLocation();
  const [captureStatus, setCaptureStatus] = useState("");
  const [captureError, setCaptureError] = useState("");
  useSeo("Shop", "Direct-sales options for signed copies, bundles, library orders, and manual/event sales.");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paypalState = params.get("paypal");
    const orderId = params.get("orderId") || "";
    const token = params.get("token") || "";
    if (paypalState === "cancelled") {
      setCaptureStatus("PayPal checkout was cancelled before payment.");
      return;
    }
    if (paypalState !== "approved" || !orderId || !token) return;
    setCaptureStatus("Confirming PayPal payment...");
    setCaptureError("");
    apiPost<Record<string, unknown>>("/api/payments/paypal/capture", {
      orderId,
      paypalOrderId: token
    })
      .then(() => {
        setCaptureStatus("Payment received. Castlehale will route the order into fulfillment.");
        window.history.replaceState(null, "", "/shop");
      })
      .catch((err) => {
        setCaptureError(err instanceof Error ? err.message : "Unable to capture PayPal payment");
      });
  }, [location.search]);

  return (
    <>
      <PageHero eyebrow="Direct sales" title="Order Castlehale titles." copy="Use PayPal checkout for listed books, or send the publisher a private request for signed copies, bundles, event stock, book-club quantities, or non-US shipping." />
      {(captureStatus || captureError) && (
        <Section title="Order status">
          {captureStatus && <p className="form-status success">{captureStatus}</p>}
          {captureError && <p className="form-status error">{captureError}</p>}
        </Section>
      )}
      <Section title="Checkout" intro="PayPal processes the payment. Castlehale keeps the order record and routes paid orders to fulfillment review.">
        <PayPalOrderForm books={catalog.books} loading={catalog.loading} error={catalog.error} />
      </Section>
      <Section title="Private order request">
        <ReaderLeadForm books={catalog.books} />
      </Section>
    </>
  );
}

function LibrariesPage() {
  useSeo("Libraries", "Castlehale Press ordering and acquisition information for library staff.");
  return (
    <>
      <PageHero eyebrow="Libraries" title="Acquisition staff get a clean path in." copy="Requests route to the publisher for ordering details, title data, examination-copy questions, and collection-development follow-up." />
      <ChecklistSection
        title="What selectors can request"
        items={["Publisher contact for collection-development questions", "Title, edition, ISBN, format, and availability details", "Ordering paths for direct, distributor, or retail availability", "Review-copy and examination-copy request routing", "Series and author information for selector review", "Rights and permissions routing when a library needs more than ordering data"]}
      />
      <Section title="Institutional inquiry" intro="This message goes to the publisher dashboard. It does not create a public account or contact authors directly.">
        <LibraryLeadForm />
      </Section>
    </>
  );
}

function PressPage() {
  useSeo("Press", "Castlehale Press press materials for publisher, authors, and books.");
  return (
    <>
      <PageHero eyebrow="Press" title="Approved materials, not guesswork." copy="Publisher boilerplate, author biographies, title sheets, release notes, and image assets are gathered here when they are ready for public use." />
      <Section title="Available files">
        <div className="operations-grid">
          {["Publisher boilerplate", "Author bios", "Book sell sheets", "Press release drafts", "Rights sheets", "Library ordering sheets"].map((item) => (
            <article className="operation" key={item}>
              <Download size={23} />
              <h3>{item}</h3>
              <p>Prepared from approved publisher materials and reviewed before public distribution.</p>
            </article>
          ))}
        </div>
      </Section>
    </>
  );
}

function RightsPage() {
  useSeo("Rights", "Castlehale Press rights posture for selective publishing and author-service clients.");
  const rightsRows = [
    ["Copyright", "The author or contract-defined owner remains explicit in writing."],
    ["Formats", "Print, ebook, audio, large print, translation, and adaptation rights are separated."],
    ["Territory", "Rights are tracked by market and language instead of bundled vaguely."],
    ["Term", "Duration, renewal, termination, and reversion belong in the written agreement."],
    ["Imprint use", "Castlehale imprint use is permissioned and distinct from service-only production."],
    ["Permissions", "Libraries, educators, media, and adaptation inquiries route to the publisher."]
  ];
  return (
    <>
      <PageHero eyebrow="Rights" title="No hidden rights grab." copy="Copyright, imprint use, royalty terms, formats, territory, and duration are handled in writing before publication work begins." />
      <Section title="Two tracks">
        <div className="two-col">
          <article className="info-panel">
            <h3>Service clients</h3>
            <p>{businessCopy.rightsService}</p>
          </article>
          <article className="info-panel">
            <h3>Castlehale Select</h3>
            <p>{businessCopy.rightsSelect}</p>
          </article>
        </div>
      </Section>
      <Section title="Rights ledger">
        <div className="rights-ledger">
          {rightsRows.map(([label, copy]) => (
            <div key={label}>
              <strong>{label}</strong>
              <span>{copy}</span>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function PublishWithUsPage() {
  useSeo("Publish With Castlehale", "Professional publishing services for authors seeking contracted publication support.");
  return (
    <>
      <PageHero eyebrow="Publish With Castlehale" title="Contract first. Files later." copy="We sell finished-book support: editing coordination, interiors, covers, editions, retailer copy, distribution setup, direct sales, launch planning, and author portal access." />
      <Section title="Services sold" intro={businessCopy.value}>
        <div className="pillar-grid">
          {servicePillars.map(({ title, icon: Icon, items }) => (
            <article className="pillar" key={title}>
              <Icon size={24} />
              <h3>{title}</h3>
              <ul>
                {items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </Section>
      <ChecklistSection title="Boundaries" items={noPromises} />
      <Section title="Request review" intro="Use the form to start a consult. If Castlehale accepts the project, materials are requested later under contract.">
        <Link className="button primary" to="/submissions">
          Send a project request <ArrowRight size={17} />
        </Link>
      </Section>
    </>
  );
}

function ServicesPage() {
  useSeo("Services", "Castlehale Press publishing services for manuscript production, channel setup, direct sales, library materials, and reporting.");
  return (
    <>
      <PageHero eyebrow="Services" title="Production work with an operating system behind it." copy="Covers, interiors, edition planning, channel setup, direct sales, library materials, launch assets, and reporting are scoped as deliverables." />
      <Section title="Capabilities">
        <div className="pillar-grid">
          {servicePillars.map(({ title, icon: Icon, items }) => (
            <article className="pillar" key={title}>
              <Icon size={24} />
              <h3>{title}</h3>
              <ul>
                {items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </Section>
      <ChecklistSection title="Individual deliverables" items={alaCarteServices} />
    </>
  );
}

function PricingPage() {
  useSeo("Pricing", "Castlehale Press publishing services pricing and high-value author support packages.");
  return (
    <>
      <PageHero eyebrow="Pricing" title="Packages priced for serious publication work." copy="Scope, deposit, balance, timeline, rights position, and deliverables are defined before book files are requested." />
      <Section title="Core offers">
        <div className="package-grid">
          {publishingPackages.map((pkg) => (
            <PackageCard key={pkg.slug} pkg={pkg} />
          ))}
        </div>
      </Section>
      <Section title="Quote path" intro="The project form starts a sales conversation and creates a lead in the publisher dashboard.">
        <Link className="button primary" to="/submissions">
          Get a quote
        </Link>
      </Section>
    </>
  );
}

function AuthorServicesPage() {
  useSeo("Author Services", "Publishing services for writers preparing books for sale.");
  const blocks = [
    ["What We Do", "Files, edition planning, covers, channel setup, direct-sales pages, library assets, launch systems, reporting, and handoff."],
    ["Who We Help", "Authors with finished manuscripts, series owners, backlist builders, and clients preparing books for sale."],
    ["Why Castlehale", "Finished files, clean product pages, sales visibility, direct reader paths, and approved public assets."],
    ["Clear Packages", "Packages are scoped, priced, and defined by deliverables."],
    ["Rights and Ownership", businessCopy.rightsService],
    ["Sales Channels", "Retailer setup, distributor guidance, Stripe/PayPal links, signed-copy flows, bundles, manual orders, and event sales."],
    ["Reporting and Author Visibility", "Authors can view their books, sales summaries, public assets, statements, launch tasks, and publisher notes."],
    ["Library and Direct Sales", "Sell sheets, library orders, review-copy tracking, inventory, shipping, and tax tracking are part of the system."],
    ["Start a Project", "The form collects fit, rights, budget, timeline, and service needs before any manuscript review."]
  ];
  return (
    <>
      <PageHero eyebrow="Author Services" title="Professional publishing support for authors." copy="Clear deliverables, written scope, and practical help getting a book ready for sale." />
      <Section title="Author-service principles">
        <div className="info-grid">
          {blocks.map(([title, copy]) => (
            <article className="info-panel" key={title}>
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </Section>
      <ChecklistSection title="What We Do Not Do" items={noPromises} />
    </>
  );
}

function ComparePage() {
  useSeo("Compare", "Compare Castlehale Press publishing services with typical author-services packages.");
  return (
    <>
      <PageHero eyebrow="Compare" title={businessCopy.compareHeadline} copy={businessCopy.compare} />
      <Section title="Castlehale vs. a typical author-services package">
        <div className="comparison">
          {comparisonRows.map(([generic, castlehale]) => (
            <div className="comparison-row" key={generic}>
              <div>
                <small>Generic package</small>
                <p>{generic}</p>
              </div>
              <div>
                <small>Castlehale focus</small>
                <p>{castlehale}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}

function SubmissionsPage() {
  useSeo("Start a Project", "Send a publishing project request to Castlehale Press. No public manuscript uploads.");
  return (
    <>
      <PageHero eyebrow="Inquiry" title="Tell us what you are building before sending files." copy="Start with fit, rights, timeline, budget, and the shape of the work. Manuscripts are requested only after the project is reviewed." />
      <InquiryForm />
    </>
  );
}

function ContactPage() {
  useSeo("Contact", "Contact Castlehale Press for books, services, rights, library, press, and author inquiries.");
  return (
    <>
      <PageHero eyebrow="Contact" title="One front door for public requests." copy="Reader orders, publishing consults, permissions, acquisitions, and media requests route through the publisher dashboard." />
      <Section title="Choose the lane">
        <div className="contact-paths">
          <Link className="route-row" to="/shop">
            <ShoppingBag size={22} />
            <span><strong>Books and signed copies</strong><small>Ask about current direct offers and availability.</small></span>
          </Link>
          <Link className="route-row" to="/submissions">
            <PenLine size={22} />
            <span><strong>Publishing consult</strong><small>Request a scoped publishing services conversation.</small></span>
          </Link>
          <Link className="route-row" to="/rights">
            <ShieldCheck size={22} />
            <span><strong>Rights inquiry</strong><small>Permissions, licensing, audio, translation, and adaptation questions.</small></span>
          </Link>
          <Link className="route-row" to="/libraries">
            <Library size={22} />
            <span><strong>Library request</strong><small>Acquisitions, review copies, and institutional ordering questions.</small></span>
          </Link>
          <Link className="route-row" to="/press">
            <Download size={22} />
            <span><strong>Press request</strong><small>Approved publisher, author, and title materials.</small></span>
          </Link>
          <Link className="route-row" to="/login">
            <Lock size={22} />
            <span><strong>Author/client portal help</strong><small>Authorized accounts only.</small></span>
          </Link>
        </div>
      </Section>
    </>
  );
}

function FaqPage() {
  useSeo("FAQ", "Frequently asked questions about Castlehale Press publishing services, rights, channels, reporting, and author portals.");
  const faqs = [
    ["Is Castlehale a vanity press?", "No. Castlehale is a boutique publishing partner and selective publisher. Paid services are contracted services with clear deliverables; Castlehale Select is separate and invitation/contract based."],
    ["Do authors keep copyright for service work?", "Yes. For author-services clients, the author keeps copyright. Deliverable ownership and ISBN/imprint use are defined by contract."],
    ["Do you guarantee sales or bestseller status?", "No. Castlehale does not guarantee bestseller status, reviews, bookstore stocking, or sales outcomes."],
    ["Do you publish every service client under the Castlehale imprint?", "No. Service clients and Castlehale-published authors are separate tracks unless a written Select publishing agreement says otherwise."],
    ["Can authors log in?", "Yes. Invited authors and service clients can access a private portal for their own books, projects, assets, statements, and reports."],
    ["Which login providers are supported?", "Invited users may sign in with supported identity providers once their account is approved."]
  ];
  return (
    <>
      <PageHero eyebrow="FAQ" title="Plain terms before work begins." copy="No sales guarantees, hidden rights grabs, vague deliverables, or open manuscript intake." />
      <Section title="Questions">
        <div className="faq-list">
          {faqs.map(([question, answer]) => (
            <details key={question}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </Section>
    </>
  );
}

function PortalPage({ kind }: { kind: "admin" | "author" }) {
  const [user, setUser] = useState<PortalUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState("");

  useSeo(kind === "admin" ? "Admin Portal" : "Author Portal", "Invite-only Castlehale Press portal.");

  useEffect(() => {
    let active = true;
    apiGet<{ user: PortalUser | null }>("/api/auth/me")
      .then((result) => {
        if (active) setUser(result.user);
      })
      .catch(() => {
        if (active) setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const load = kind === "admin"
      ? Promise.all([apiGet<Record<string, unknown>>("/api/admin/summary"), apiGet<Record<string, unknown>>("/api/admin/platform")]).then(
          ([summary, platform]) => ({ ...summary, ...platform })
        )
      : apiGet<Record<string, unknown>>("/api/author/dashboard");
    load
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Unable to load portal data"));
  }, [kind, user]);

  const allowed =
    kind === "admin" ? user && ["owner", "admin", "staff"].includes(user.role) : user && ["owner", "admin", "staff", "author", "readonly", "client"].includes(user.role);

  if (loading) return <PageHero eyebrow="Secure portal" title="Checking session..." copy="One moment while Castlehale verifies access." />;
  if (!user) return <LoginPanel kind={kind} />;
  if (!allowed) return <PageHero eyebrow="Access denied" title="This account is not scoped for this portal." copy="Ask Castlehale Press to update your role or author/client assignment." />;

  return (
    <section className="portal-page-shell">
      <header className="portal-header">
        <div>
          <p className="eyebrow">{kind === "admin" ? "Admin console" : "Author console"}</p>
          <h1>{kind === "admin" ? "Publisher operations" : "Author workspace"}</h1>
          <p>{kind === "admin" ? "Leads, catalog records, platform connections, messages, imports, and manual data controls." : "Books, sales summaries, files, messages, publication tasks, and platform connection records scoped to this author account."}</p>
        </div>
        <div className="portal-user-badge">
          <small>Signed in as</small>
          <strong>{user.name || "Castlehale user"}</strong>
          <span>{humanize(user.role)}</span>
        </div>
      </header>
      {error && <p className="form-status error">{error}</p>}
      <div className="portal-dashboard-body">
        {data ? <PortalData data={data} kind={kind} /> : <p className="note">Loading your workspace...</p>}
      </div>
    </section>
  );
}

function LoginPanel({ kind }: { kind: "admin" | "author" }) {
  return (
    <section className="portal-login">
      <div>
        <p className="eyebrow">Invite-only access</p>
        <h1>{kind === "admin" ? "Admin login" : "Author/client login"}</h1>
        <p>
          Sign in with an approved identity provider. Accounts must be authorized by Castlehale before access is granted.
        </p>
      </div>
      <ProviderButtons redirect={kind === "admin" ? "/admin" : "/author"} />
    </section>
  );
}

function LoginPage() {
  useSeo("Login", "Invited Castlehale Press account login for owner, staff, authors, clients, and reviewers.");
  return (
    <section className="login-page">
      <div className="login-page-copy">
        <p className="eyebrow">Authorized accounts only</p>
        <h1>Login to Castlehale</h1>
        <p>
          Accounts are created by the publisher first. Use the same email that was authorized for your Castlehale role, then connect it with your OAuth provider.
        </p>
        <div className="login-scope-grid">
          <div>
            <strong>Publisher</strong>
            <span>Full press dashboard, leads, authors, catalog, packages, and platform connections.</span>
          </div>
          <div>
            <strong>Author</strong>
            <span>Your books, files, sales, ad metrics, buyer funnels, publication tasks, and publisher messages.</span>
          </div>
          <div>
            <strong>Staff</strong>
            <span>Assigned production work, files, messages, imports, metadata, and distribution jobs.</span>
          </div>
        </div>
      </div>
      <ProviderButtons redirect="/login" />
    </section>
  );
}

function ProviderButtons({ redirect }: { redirect: string }) {
  const [providers, setProviders] = useState<AuthProvider[]>([]);
  const [error, setError] = useState("");
  const localDevLogin = typeof window !== "undefined" && ["127.0.0.1", "localhost"].includes(window.location.hostname);
  useEffect(() => {
    apiGet<{ providers: AuthProvider[] }>("/api/auth/providers")
      .then((result) => setProviders(result.providers))
      .catch((err) => setError(err instanceof Error ? err.message : "Unable to load login providers"));
  }, []);
  function startLocalSession(kind: "owner" | "author") {
    const target = redirect === "/login" ? (kind === "owner" ? "/admin" : "/author") : redirect;
    const endpoint = kind === "owner" ? "dev-owner" : "dev-author";
    window.location.href = `/api/auth/${endpoint}/start?redirect=${encodeURIComponent(target)}`;
  }
  const showLocalAdmin = localDevLogin && (redirect === "/admin" || redirect === "/login");
  const showLocalAuthor = localDevLogin && (redirect === "/author" || redirect === "/login");
  return (
    <div className="login-card">
      <Lock size={28} />
      <h2>Choose a provider</h2>
      <div className="provider-list">
        {error && <p className="form-status error">{error}</p>}
        {!error && providers.length === 0 && <p className="note">Loading authorized login providers...</p>}
        {providers.map((provider) => (
          <button key={provider.id} onClick={() => authStart(provider.id, redirect)} className="provider-button" disabled={!provider.configured}>
            <KeyRound size={18} />
            Continue with {provider.label}
            {!provider.configured && <small>not available</small>}
          </button>
        ))}
        {showLocalAdmin && (
          <button type="button" onClick={() => startLocalSession("owner")} className="provider-button">
            <KeyRound size={18} />
            Local admin session
            <small>localhost only</small>
          </button>
        )}
        {showLocalAuthor && (
          <button type="button" onClick={() => startLocalSession("author")} className="provider-button">
            <KeyRound size={18} />
            Local Johna author session
            <small>localhost only</small>
          </button>
        )}
      </div>
      <p className="note">Use the identity provider connected to your Castlehale invitation.</p>
    </div>
  );
}

function PortalData({ data, kind }: { data: Record<string, unknown>; kind: "admin" | "author" }) {
  const sales = arrayOfRecords(data.salesSummary);
  const ads = arrayOfRecords(data.advertisingMetrics);
  const funnels = arrayOfRecords(data.buyerFunnels);
  const packages = arrayOfRecords(data.publicationPackages);
  const totalUnits = sales.reduce((sum, row) => sum + numberField(row, "units"), 0);
  const totalSales = sales.reduce((sum, row) => sum + numberField(row, "gross"), 0);
  const adSpend = ads.reduce((sum, row) => sum + numberField(row, "spend"), 0);
  const adRevenue = ads.reduce((sum, row) => sum + numberField(row, "revenue"), 0);
  const funnelPurchases = funnels
    .filter((row) => stringField(row, "funnel_step") === "purchase")
    .reduce((sum, row) => sum + numberField(row, "event_count"), 0);
  const metrics =
    kind === "admin"
      ? [
          ["Gross revenue", money(Number(data.grossRevenue ?? 0))],
          ["Estimated net", money(Number(data.estimatedNetRevenue ?? 0))],
          ["Units", String(Number(data.unitsByChannel ?? 0))],
          ["Open leads", String(Number(data.openServiceLeads ?? 0))],
          ["Authors", String(Number(data.activeAuthors ?? 0))],
          ["Packages", String(Number(data.activePublicationPackages ?? packages.length))]
        ]
      : [
          ["Visible books", String(arrayOfRecords(data.books).length)],
          ["Units sold", String(totalUnits)],
          ["Gross sales", money(totalSales)],
          ["Ad spend", money(adSpend)],
          ["Ad revenue", money(adRevenue)],
          ["Purchases", String(funnelPurchases)]
        ];

  if (kind === "admin") {
    return <AdminDirectorsConsole data={data} metrics={metrics} />;
  }

  return <AuthorProducersConsole data={data} metrics={metrics} />;
}

type DirectorTab = {
  key: string;
  label: string;
  children: ReactNode;
};

type DirectorPage = {
  key: string;
  label: string;
  icon: ReactNode;
  title: string;
  copy: string;
  tabs: DirectorTab[];
};

function AdminDirectorsConsole({ data, metrics }: { data: Record<string, unknown>; metrics: string[][] }) {
  const [activePageKey, setActivePageKey] = useState("command");
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});

  const pages: DirectorPage[] = [
    {
      key: "command",
      label: "Command",
      icon: <BarChart3 size={17} />,
      title: "Director's console",
      copy: "A tight operating view for revenue, leads, messages, access, and the next work that needs attention.",
      tabs: [
        {
          key: "today",
          label: "Today",
          children: (
            <div className="director-grid two">
              <DirectorMetricStrip metrics={metrics} />
              <AdminInbox />
              <DashboardLists data={data} kind="admin" />
            </div>
          )
        },
        {
          key: "access",
          label: "Access",
          children: <AdminControlPanel data={data} />
        }
      ]
    },
    {
      key: "orders",
      label: "Orders",
      icon: <ReceiptText size={17} />,
      title: "Sales and orders",
      copy: "Direct checkout, payment state, fulfillment queue, provider credentials, and payment events.",
      tabs: [
        { key: "orders", label: "Orders", children: <CommerceOperationsPanel initialTab="orders" showTabs={false} /> },
        { key: "fulfillment", label: "Fulfillment", children: <CommerceOperationsPanel initialTab="fulfillment" showTabs={false} /> },
        { key: "payments", label: "Payments", children: <CommerceOperationsPanel initialTab="payments" showTabs={false} /> },
        { key: "providers", label: "Providers", children: <CommerceOperationsPanel initialTab="providers" showTabs={false} /> }
      ]
    },
    {
      key: "catalog",
      label: "Catalog",
      icon: <BookOpen size={17} />,
      title: "Catalog and media",
      copy: "Books, editions, ISBNs, cover assets, author-facing metadata, and public catalog records.",
      tabs: [
        { key: "books", label: "Books", children: <CatalogManagementWorkspace /> },
        { key: "data", label: "Data console", children: <AdminDataConsole /> }
      ]
    },
    {
      key: "authors",
      label: "Authors",
      icon: <Users size={17} />,
      title: "Authors and accounts",
      copy: "Invite accounts, scope author access, manage author-visible catalog records, and keep staff communication in one place.",
      tabs: [
        { key: "access", label: "Access", children: <AdminControlPanel data={data} /> },
        { key: "profiles", label: "Profiles", children: <CatalogManagementWorkspace /> },
        { key: "messages", label: "Messages", children: <MessageWorkspace data={data} kind="admin" /> }
      ]
    },
    {
      key: "leads",
      label: "Leads",
      icon: <Mail size={17} />,
      title: "Leads and messages",
      copy: "Publishing inquiries, library acquisition leads, author/staff threads, notifications, and follow-up.",
      tabs: [
        { key: "inbox", label: "Inbox", children: <AdminInbox /> },
        { key: "threads", label: "Threads", children: <MessageWorkspace data={data} kind="admin" /> }
      ]
    },
    {
      key: "pipeline",
      label: "Pipeline",
      icon: <PackageCheck size={17} />,
      title: "Publishing pipeline",
      copy: "Publication packages, channel jobs, production files, distribution status, and automation notes.",
      tabs: [
        { key: "packages", label: "Packages", children: <PublicationWorkspace data={data} /> },
        { key: "channels", label: "Channels", children: <ConnectionPanel data={data} kind="admin" /> },
        { key: "imports", label: "Imports", children: <ImportPreview /> }
      ]
    },
    {
      key: "finance",
      label: "Finance",
      icon: <FileSpreadsheet size={17} />,
      title: "Finance and reporting",
      copy: "Sales imports, ad metrics, buyer funnels, payment events, and reporting health.",
      tabs: [
        { key: "reports", label: "Reports", children: <DashboardLists data={data} kind="admin" /> },
        { key: "payments", label: "Payments", children: <CommerceOperationsPanel initialTab="payments" showTabs={false} /> },
        { key: "imports", label: "Imports", children: <ImportPreview /> }
      ]
    },
    {
      key: "settings",
      label: "Settings",
      icon: <KeyRound size={17} />,
      title: "Settings and integrations",
      copy: "Provider secrets, OAuth-adjacent connection records, data tools, and operational configuration.",
      tabs: [
        { key: "providers", label: "Secrets", children: <CommerceOperationsPanel initialTab="providers" showTabs={false} /> },
        { key: "accounts", label: "Accounts", children: <AdminControlPanel data={data} /> },
        { key: "data", label: "Data tools", children: <AdminDataConsole /> }
      ]
    }
  ];

  const activePage = pages.find((page) => page.key === activePageKey) ?? pages[0];
  const activeTabKey = activeTabs[activePage.key] ?? activePage.tabs[0]?.key;
  const activeTab = activePage.tabs.find((tab) => tab.key === activeTabKey) ?? activePage.tabs[0];

  function setActiveTab(key: string) {
    setActiveTabs((current) => ({ ...current, [activePage.key]: key }));
  }

  return (
    <div className="director-console">
      <aside className="director-sidebar" aria-label="Admin console sections">
        <div className="director-sidebar-head">
          <strong>Castlehale</strong>
          <span>Director Console</span>
        </div>
        <nav>
          {pages.map((page) => (
            <button
              className={page.key === activePage.key ? "active" : ""}
              key={page.key}
              onClick={() => setActivePageKey(page.key)}
              type="button"
            >
              {page.icon}
              <span>{page.label}</span>
            </button>
          ))}
        </nav>
      </aside>
      <section className="director-main">
        <header className="director-page-head">
          <div>
            <p className="eyebrow">{activePage.label}</p>
            <h2>{activePage.title}</h2>
            <p>{activePage.copy}</p>
          </div>
          <DirectorMetricStrip metrics={metrics.slice(0, 4)} compact />
        </header>
        <div className="director-tabs" role="tablist" aria-label={`${activePage.label} tabs`}>
          {activePage.tabs.map((tab) => (
            <button
              aria-selected={tab.key === activeTab.key}
              className={tab.key === activeTab.key ? "active" : ""}
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="director-panel" role="tabpanel">
          {activeTab?.children}
        </div>
      </section>
    </div>
  );
}

function DirectorMetricStrip({ metrics, compact = false }: { metrics: string[][]; compact?: boolean }) {
  return (
    <div className={compact ? "director-metric-strip compact" : "director-metric-strip"}>
      {metrics.map(([label, value], index) => (
        <article className={index < 2 ? "primary" : ""} key={label}>
          <small>{label}</small>
          <strong>{value}</strong>
        </article>
      ))}
    </div>
  );
}

function AuthorProducersConsole({ data, metrics }: { data: Record<string, unknown>; metrics: string[][] }) {
  const [activePageKey, setActivePageKey] = useState("works");
  const [activeTabs, setActiveTabs] = useState<Record<string, string>>({});
  const books = arrayOfRecords(data.books);
  const packages = arrayOfRecords(data.publicationPackages);
  const pipeline = arrayOfRecords(data.pipeline);
  const sales = arrayOfRecords(data.salesSummary);

  const pages: DirectorPage[] = [
    {
      key: "works",
      label: "Works",
      icon: <BookOpen size={17} />,
      title: "Producer console",
      copy: "A scoped operating view for this author's books, metadata, covers, editions, production state, and public catalog presence.",
      tabs: [
        {
          key: "overview",
          label: "Overview",
          children: (
            <div className="director-grid two">
              <DirectorMetricStrip metrics={metrics} />
              <AuthorWorksOverview books={books} packages={packages} pipeline={pipeline} />
              <DashboardLists data={data} kind="author" />
            </div>
          )
        },
        { key: "catalog", label: "Catalog", children: <CatalogManagementWorkspace /> }
      ]
    },
    {
      key: "production",
      label: "Production",
      icon: <PackageCheck size={17} />,
      title: "Production and releases",
      copy: "Publication packages, uploaded files, distribution channel jobs, and production tasks tied to this account.",
      tabs: [
        { key: "packages", label: "Packages", children: <PublicationWorkspace data={data} /> },
        { key: "tasks", label: "Tasks", children: <DashboardLists data={data} kind="author" /> }
      ]
    },
    {
      key: "sales",
      label: "Sales",
      icon: <BarChart3 size={17} />,
      title: "Sales and audience",
      copy: "Sales summaries, ad metrics, buyer funnels, and report imports for this author's books only.",
      tabs: [
        { key: "reports", label: "Reports", children: <DashboardLists data={data} kind="author" /> },
        { key: "imports", label: "Imports", children: <ImportPreview /> }
      ]
    },
    {
      key: "platforms",
      label: "Platforms",
      icon: <PlugZap size={17} />,
      title: "Platform connections",
      copy: "KDP, advertising, social, retailer, and distributor connection records scoped to this author account.",
      tabs: [
        { key: "connections", label: "Connections", children: <ConnectionPanel data={data} kind="author" /> },
        { key: "reports", label: "Report imports", children: <ImportPreview /> }
      ]
    },
    {
      key: "messages",
      label: "Messages",
      icon: <MessageSquare size={17} />,
      title: "Publisher messages",
      copy: "Two-way private communication between the author account and Castlehale staff.",
      tabs: [
        { key: "threads", label: "Threads", children: <MessageWorkspace data={data} kind="author" /> }
      ]
    }
  ];

  const activePage = pages.find((page) => page.key === activePageKey) ?? pages[0];
  const activeTabKey = activeTabs[activePage.key] ?? activePage.tabs[0]?.key;
  const activeTab = activePage.tabs.find((tab) => tab.key === activeTabKey) ?? activePage.tabs[0];

  function setActiveTab(key: string) {
    setActiveTabs((current) => ({ ...current, [activePage.key]: key }));
  }

  return (
    <div className="director-console producer-console">
      <aside className="director-sidebar" aria-label="Author producer console sections">
        <div className="director-sidebar-head">
          <strong>Author</strong>
          <span>Producer Console</span>
        </div>
        <nav>
          {pages.map((page) => (
            <button
              className={page.key === activePage.key ? "active" : ""}
              key={page.key}
              onClick={() => setActivePageKey(page.key)}
              type="button"
            >
              {page.icon}
              <span>{page.label}</span>
            </button>
          ))}
        </nav>
      </aside>
      <section className="director-main">
        <header className="director-page-head">
          <div>
            <p className="eyebrow">{activePage.label}</p>
            <h2>{activePage.title}</h2>
            <p>{activePage.copy}</p>
          </div>
          <DirectorMetricStrip metrics={metrics.slice(0, 4)} compact />
        </header>
        <div className="director-tabs" role="tablist" aria-label={`${activePage.label} tabs`}>
          {activePage.tabs.map((tab) => (
            <button
              aria-selected={tab.key === activeTab.key}
              className={tab.key === activeTab.key ? "active" : ""}
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="director-panel" role="tabpanel">
          {activeTab?.children}
        </div>
      </section>
    </div>
  );
}

function AuthorWorksOverview({
  books,
  packages,
  pipeline
}: {
  books: Record<string, unknown>[];
  packages: Record<string, unknown>[];
  pipeline: Record<string, unknown>[];
}) {
  return (
    <article className="data-panel author-works-overview">
      <h3>
        <BookOpen size={19} /> Works in scope
      </h3>
      <div className="compact-table">
        {books.map((book) => (
          <div key={stringField(book, "id") || stringField(book, "slug")}>
            <span>{stringField(book, "title")}</span>
            <strong>{humanize(stringField(book, "publication_status") || stringField(book, "publicationStatus") || "tracked")}</strong>
          </div>
        ))}
        {books.length === 0 && <p className="note">No books are assigned to this account yet.</p>}
      </div>
      <div className="producer-count-row">
        <span>{packages.length} package(s)</span>
        <span>{pipeline.filter((row) => numberField(row, "blocked")).length} blocker(s)</span>
      </div>
    </article>
  );
}

function CommerceOperationsPanel({
  initialTab = "orders",
  showTabs = true
}: {
  initialTab?: "orders" | "fulfillment" | "payments" | "providers";
  showTabs?: boolean;
}) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("paypal");
  const [activeTab, setActiveTab] = useState(initialTab);

  async function loadOperations() {
    setError("");
    try {
      setData(await apiGet<Record<string, unknown>>("/api/admin/operations"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load commerce operations");
    }
  }

  useEffect(() => {
    void loadOperations();
  }, []);

  const orders = arrayOfRecords(data?.orders);
  const orderItems = arrayOfRecords(data?.orderItems);
  const fulfillmentJobs = arrayOfRecords(data?.fulfillmentJobs);
  const paymentEvents = arrayOfRecords(data?.paymentEvents);
  const credentialRecords = arrayOfRecords(data?.credentialRecords);
  const requirements = arrayOfRecords(data?.providerRequirements);
  const selectedRequirement = requirements.find((item) => stringField(item, "provider") === selectedProvider) ?? requirements[0];
  const credentialFields = arrayOfStrings(selectedRequirement?.fields);
  const paidOrders = orders.filter((order) => stringField(order, "payment_status") === "paid");
  const tabs = [
    ["orders", "Orders"],
    ["fulfillment", "Fulfillment"],
    ["payments", "Payments"],
    ["providers", "Providers"]
  ] as const;

  async function saveCredential(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const provider = String(form.get("provider") || selectedProvider);
    const requirement = requirements.find((item) => stringField(item, "provider") === provider);
    const fields = arrayOfStrings(requirement?.fields);
    const credentials = Object.fromEntries(fields.map((field) => [field, form.get(field)]));
    setStatus("");
    setError("");
    try {
      await apiPost("/api/admin/credentials", {
        provider,
        credentialLabel: form.get("credentialLabel") || "primary",
        environment: form.get("environment"),
        credentials
      });
      setStatus(`${humanize(provider)} credentials saved.`);
      event.currentTarget.reset();
      await loadOperations();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save provider credentials");
    }
  }

  async function createFulfillment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setStatus("");
    setError("");
    try {
      await apiPost("/api/admin/fulfillment-jobs", {
        orderId: form.get("orderId"),
        provider: form.get("provider"),
        submitNow: form.get("submitNow") === "on",
        request: {
          title: form.get("title"),
          quantity: form.get("quantity"),
          podPackageId: form.get("podPackageId"),
          coverPdfUrl: form.get("coverPdfUrl"),
          interiorPdfUrl: form.get("interiorPdfUrl"),
          shippingLevel: form.get("shippingLevel")
        }
      });
      setStatus("Fulfillment job saved.");
      event.currentTarget.reset();
      await loadOperations();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save fulfillment job");
    }
  }

  async function submitJob(jobId: string) {
    setStatus("");
    setError("");
    try {
      await apiPost(`/api/admin/fulfillment-jobs/${encodeURIComponent(jobId)}/submit`, {});
      setStatus("Fulfillment job submitted.");
      await loadOperations();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to submit fulfillment job");
    }
  }

  return (
    <article className="data-panel commerce-ops-panel director-surface">
      <div className="panel-heading-row">
        <h3>
          <ReceiptText size={19} /> Sales operations
        </h3>
        <button className="button small" type="button" onClick={loadOperations}>
          Refresh
        </button>
      </div>
      {error && <p className="form-status error">{error}</p>}
      {status && <p className="form-status success">{status}</p>}
      {!data && !error && <p className="note">Loading orders, payments, fulfillment, and provider credentials...</p>}
      {data && (
        <>
          <div className="ops-summary-grid">
            <div>
              <small>Orders</small>
              <strong>{orders.length}</strong>
            </div>
            <div>
              <small>Paid</small>
              <strong>{paidOrders.length}</strong>
            </div>
            <div>
              <small>Fulfillment jobs</small>
              <strong>{fulfillmentJobs.length}</strong>
            </div>
            <div>
              <small>Credentials</small>
              <strong>{credentialRecords.length}</strong>
            </div>
          </div>
          {showTabs && (
            <div className="commerce-tabs" role="tablist" aria-label="Commerce operations">
              {tabs.map(([key, label]) => (
                <button
                  aria-selected={activeTab === key}
                  className={activeTab === key ? "active" : ""}
                  key={key}
                  onClick={() => setActiveTab(key)}
                  role="tab"
                  type="button"
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {activeTab === "orders" && (
            <article className="commerce-table-panel">
              <div className="commerce-table-head">
                <span>Order</span>
                <span>Buyer</span>
                <span>Total</span>
                <span>Payment</span>
                <span>Fulfillment</span>
              </div>
              {orders.slice(0, 24).map((order) => {
                const items = orderItems.filter((item) => stringField(item, "order_id") === stringField(order, "id"));
                return (
                  <div className="commerce-order-row" key={stringField(order, "id")}>
                    <div>
                      <strong>{items.map((item) => stringField(item, "description")).join(", ") || `${numberField(order, "item_count")} item(s)`}</strong>
                      <small>{stringField(order, "id").slice(0, 8)} - {stringField(order, "channel")}</small>
                    </div>
                    <div>
                      <span>{stringField(order, "customer_name") || "Customer"}</span>
                      <small>{stringField(order, "customer_email")}</small>
                    </div>
                    <strong>{moneyWithCents(numberField(order, "gross_amount"))}</strong>
                    <span className={`status-pill ${statusClass(stringField(order, "payment_status"))}`}>{humanize(stringField(order, "payment_status"))}</span>
                    <span className={`status-pill ${statusClass(stringField(order, "fulfillment_status"))}`}>{humanize(stringField(order, "fulfillment_status"))}</span>
                  </div>
                );
              })}
              {orders.length === 0 && <p className="note">Direct checkout and manual orders will appear here.</p>}
            </article>
          )}
          {activeTab === "fulfillment" && (
            <div className="commerce-split">
              <form className="mini-panel" onSubmit={createFulfillment}>
                <h4>Print-on-demand job</h4>
                <label>
                  Paid order
                  <select name="orderId" required>
                    <option value="">Choose paid order</option>
                    {paidOrders.map((order) => (
                      <option key={stringField(order, "id")} value={stringField(order, "id")}>
                        {stringField(order, "customer_name") || "Customer"} - {moneyWithCents(numberField(order, "gross_amount"))}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Provider
                  <select name="provider" defaultValue="lulu_print_api">
                    <option value="lulu_print_api">Lulu Print API</option>
                    <option value="bookvault_api">Bookvault API</option>
                  </select>
                </label>
                <div className="form-grid compact">
                  <TextInput name="title" label="Print title" />
                  <TextInput name="quantity" label="Quantity" type="number" defaultValue="1" />
                  <TextInput name="shippingLevel" label="Shipping level" defaultValue="MAIL" />
                  <TextInput name="podPackageId" label="Lulu POD package ID" />
                </div>
                <label>
                  Cover PDF URL
                  <input name="coverPdfUrl" placeholder="https://..." />
                </label>
                <label>
                  Interior PDF URL
                  <input name="interiorPdfUrl" placeholder="https://..." />
                </label>
                <label className="check-field">
                  <input name="submitNow" type="checkbox" />
                  Submit immediately
                </label>
                <button className="button" type="submit">
                  Save fulfillment job
                </button>
              </form>
              <article className="mini-panel">
                <h4>Fulfillment queue</h4>
                <div className="compact-table fulfillment-table">
                  {fulfillmentJobs.map((job) => (
                    <div key={stringField(job, "id")}>
                      <span>{humanize(stringField(job, "provider"))} - {stringField(job, "customer_name") || stringField(job, "order_id")}</span>
                      <strong>{humanize(stringField(job, "status"))}{stringField(job, "last_error") ? ` - ${stringField(job, "last_error")}` : ""}</strong>
                      {stringField(job, "status") === "queued" && stringField(job, "provider") === "lulu_print_api" && (
                        <button className="button small" type="button" onClick={() => submitJob(stringField(job, "id"))}>
                          Submit
                        </button>
                      )}
                    </div>
                  ))}
                  {fulfillmentJobs.length === 0 && <p className="note">Paid checkout orders will queue here automatically.</p>}
                </div>
              </article>
            </div>
          )}
          {activeTab === "payments" && (
            <article className="commerce-table-panel payment-ledger">
              <div className="commerce-table-head">
                <span>Event</span>
                <span>Provider</span>
                <span>Amount</span>
                <span>Status</span>
                <span>Received</span>
              </div>
              {paymentEvents.slice(0, 30).map((event, index) => (
                <div className="commerce-order-row" key={`${stringField(event, "id")}-${index}`}>
                  <div>
                    <strong>{humanize(stringField(event, "event_type"))}</strong>
                    <small>{stringField(event, "provider_event_id")}</small>
                  </div>
                  <span>{humanize(stringField(event, "provider"))}</span>
                  <strong>{moneyWithCents(numberField(event, "amount"))}</strong>
                  <span className={`status-pill ${statusClass(stringField(event, "status"))}`}>{humanize(stringField(event, "status"))}</span>
                  <small>{stringField(event, "received_at")}</small>
                </div>
              ))}
              {paymentEvents.length === 0 && <p className="note">PayPal checkout and webhook events will appear here.</p>}
            </article>
          )}
          {activeTab === "providers" && (
            <div className="commerce-split provider-split">
              <form className="mini-panel" onSubmit={saveCredential}>
                <h4>Encrypted provider credential</h4>
                <label>
                  Provider
                  <select name="provider" value={selectedProvider} onChange={(event) => setSelectedProvider(event.target.value)}>
                    {requirements.map((item) => (
                      <option key={stringField(item, "provider")} value={stringField(item, "provider")}>
                        {stringField(item, "label")}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="form-grid compact">
                  <TextInput name="credentialLabel" label="Label" defaultValue="primary" />
                  <label>
                    Environment
                    <select name="environment" defaultValue="sandbox">
                      <option value="sandbox">Sandbox/test</option>
                      <option value="production">Production/live</option>
                    </select>
                  </label>
                </div>
                {credentialFields.map((field) => (
                  <label key={field}>
                    {humanize(field)}
                    <input name={field} type={field.toLowerCase().includes("secret") || field.toLowerCase().includes("key") || field.toLowerCase().includes("token") ? "password" : "text"} required />
                  </label>
                ))}
                <button className="button primary" type="submit">
                  Save credentials
                </button>
                <p className="note">Stored encrypted server-side. Values are never sent back to the browser.</p>
              </form>
              <article className="mini-panel">
                <h4>Credential records</h4>
                <div className="compact-table">
                  {credentialRecords.map((record) => (
                    <div key={stringField(record, "id")}>
                      <span>{humanize(stringField(record, "provider"))} - {stringField(record, "credential_label")}</span>
                      <strong>{humanize(stringField(record, "environment"))} - {humanize(stringField(record, "status"))}</strong>
                    </div>
                  ))}
                  {credentialRecords.length === 0 && <p className="note">No provider credentials saved yet.</p>}
                </div>
              </article>
            </div>
          )}
        </>
      )}
    </article>
  );
}

function AdminControlPanel({ data }: { data: Record<string, unknown> }) {
  const authors = arrayOfRecords(data.authors);
  const [inviteStatus, setInviteStatus] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("");

  async function inviteUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setInviteStatus("");
    const form = new FormData(event.currentTarget);
    try {
      await apiPost("/api/admin/users", {
        email: form.get("email"),
        name: form.get("name"),
        role: form.get("role"),
        authorId: form.get("authorId") ? Number(form.get("authorId")) : undefined,
        relationship: form.get("relationship") || "author",
        canViewFinancials: form.get("canViewFinancials") === "on",
        canViewCustomerData: form.get("canViewCustomerData") === "on"
      });
      setInviteStatus("User invitation saved.");
      event.currentTarget.reset();
    } catch (err) {
      setInviteStatus(err instanceof Error ? err.message : "Unable to invite user");
    }
  }

  async function addConnection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setConnectionStatus("");
    const form = new FormData(event.currentTarget);
    try {
      await apiPost("/api/admin/connections", {
        provider: form.get("provider"),
        accountLabel: form.get("accountLabel"),
        connectionStatus: form.get("connectionStatus"),
        authType: form.get("authType")
      });
      setConnectionStatus("Connection record saved.");
      event.currentTarget.reset();
    } catch (err) {
      setConnectionStatus(err instanceof Error ? err.message : "Unable to save connection");
    }
  }

  return (
    <article className="data-panel admin-control-panel">
      <div className="panel-heading-row">
        <h3>
          <UserPlus size={19} /> Access and accounts
        </h3>
      </div>
      <div className="admin-control-grid">
        <form onSubmit={inviteUser}>
          <h4>Invite staff or author</h4>
          <label>
            Email
            <input name="email" type="email" required placeholder="author@example.com" />
          </label>
          <label>
            Name
            <input name="name" placeholder="Author name" />
          </label>
          <div className="form-grid compact">
            <label>
              Role
              <select name="role" defaultValue="author">
                {["author", "client", "staff", "readonly", "admin"].map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Author scope
              <select name="authorId" defaultValue="">
                <option value="">No author scope</option>
                {authors.map((author) => (
                  <option key={String(author.id)} value={String(author.id)}>
                    {stringField(author, "name")}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <input name="relationship" type="hidden" value="author" />
          <label className="check-field">
            <input name="canViewFinancials" type="checkbox" defaultChecked />
            Financial dashboard access
          </label>
          <label className="check-field">
            <input name="canViewCustomerData" type="checkbox" />
            Customer detail access
          </label>
          <button className="button primary" type="submit">
            Save invite
          </button>
          {inviteStatus && <p className="form-status">{inviteStatus}</p>}
        </form>
        <form onSubmit={addConnection}>
          <h4>Register platform connection</h4>
          <label>
            Provider
            <select name="provider" defaultValue="amazon_kdp">
              {["amazon_kdp", "ingramspark", "apple_books", "kobo_writing_life", "facebook", "x", "amazon_ads", "meta_ads"].map((provider) => (
                <option key={provider} value={provider}>
                  {humanize(provider)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Account label
            <input name="accountLabel" placeholder="Castlehale Press main account" required />
          </label>
          <div className="form-grid compact">
            <label>
              Status
              <select name="connectionStatus" defaultValue="not_connected">
                {["not_connected", "connected", "needs_review", "blocked"].map((status) => (
                  <option key={status} value={status}>
                    {humanize(status)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Auth
              <select name="authType" defaultValue="oauth">
                <option value="oauth">OAuth</option>
                <option value="portal">Portal</option>
                <option value="csv">CSV</option>
              </select>
            </label>
          </div>
          <button className="button" type="submit">
            Save connection
          </button>
          {connectionStatus && <p className="form-status">{connectionStatus}</p>}
        </form>
      </div>
    </article>
  );
}

type AdminEditorField = {
  name: string;
  label: string;
  type?: "text" | "textarea" | "number" | "money" | "date" | "json" | "boolean" | "select";
  required?: boolean;
  options?: string[];
  reference?: string;
  readOnly?: boolean;
};

type AdminEditorEntity = {
  key: string;
  title: string;
  idField: string;
  idType: "number" | "text";
  description: string;
  canCreate?: boolean;
  fields: AdminEditorField[];
};

type AdminEditorData = {
  entities: AdminEditorEntity[];
  records: Record<string, Record<string, unknown>[]>;
  references: Record<string, Record<string, unknown>[]>;
};

function AdminDataConsole() {
  const [data, setData] = useState<AdminEditorData | null>(null);
  const [entityKey, setEntityKey] = useState("authors");
  const [selectedId, setSelectedId] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  async function loadEditor(nextEntityKey = entityKey, nextSelectedId = selectedId) {
    setError("");
    try {
      const result = await apiGet<AdminEditorData>("/api/admin/editor");
      setData(result);
      const entity = result.entities.find((item) => item.key === nextEntityKey) ?? result.entities[0];
      if (entity) {
        setEntityKey(entity.key);
        const rows = result.records[entity.key] ?? [];
        const next = rows.find((row) => String(row[entity.idField]) === nextSelectedId) ?? rows[0];
        setSelectedId(next ? String(next[entity.idField]) : "");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load admin editor");
    }
  }

  useEffect(() => {
    void loadEditor();
  }, []);

  const entity = data?.entities.find((item) => item.key === entityKey) ?? data?.entities[0];
  const rows = entity && data ? data.records[entity.key] ?? [] : [];
  const selectedRecord = entity ? rows.find((row) => String(row[entity.idField]) === selectedId) ?? rows[0] : undefined;

  function changeEntity(nextKey: string) {
    setEntityKey(nextKey);
    const nextEntity = data?.entities.find((item) => item.key === nextKey);
    const nextRows = nextEntity && data ? data.records[nextEntity.key] ?? [] : [];
    setSelectedId(nextEntity && nextRows[0] ? String(nextRows[0][nextEntity.idField]) : "");
    setStatus("");
    setError("");
  }

  async function saveRecord(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!entity || !selectedRecord) return;
    const id = String(selectedRecord[entity.idField]);
    setStatus("");
    setError("");
    try {
      await apiPatch(`/api/admin/editor/${entity.key}/${encodeURIComponent(id)}`, {
        values: collectEditorValues(event.currentTarget, entity.fields)
      });
      setStatus(`${entity.title} record saved.`);
      await loadEditor(entity.key, id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save record");
    }
  }

  async function createRecord(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!entity?.canCreate) return;
    setStatus("");
    setError("");
    try {
      const result = await apiPost<Record<string, unknown>>(`/api/admin/editor/${entity.key}`, {
        values: collectEditorValues(event.currentTarget, entity.fields)
      });
      const id = String(result.id || "");
      setStatus(`${entity.title} record created.`);
      event.currentTarget.reset();
      await loadEditor(entity.key, id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create record");
    }
  }

  return (
    <article className="data-panel admin-editor-console">
      <div className="panel-heading-row">
        <h3>
          <ClipboardList size={19} /> Admin data console
        </h3>
      </div>
      {error && <p className="form-status error">{error}</p>}
      {status && <p className="form-status success">{status}</p>}
      {!data && !error && <p className="note">Loading editable D1 records...</p>}
      {data && entity && (
        <>
          <div className="editor-toolbar">
            <label>
              Area
              <select value={entity.key} onChange={(event) => changeEntity(event.target.value)}>
                {data.entities.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.title}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Record
              <select value={selectedRecord ? String(selectedRecord[entity.idField]) : ""} onChange={(event) => setSelectedId(event.target.value)}>
                {rows.map((row) => (
                  <option key={String(row[entity.idField])} value={String(row[entity.idField])}>
                    {editorRecordLabel(row, entity)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="note">{entity.description}</p>
          <div className="admin-editor-grid">
            <form className="mini-panel" onSubmit={saveRecord} key={`edit-${entity.key}-${selectedRecord ? String(selectedRecord[entity.idField]) : "none"}`}>
              <h4>Edit selected</h4>
              {selectedRecord ? (
                <>
                  <div className="record-id-row">
                    <small>{entity.idField}</small>
                    <strong>{String(selectedRecord[entity.idField])}</strong>
                  </div>
                  <div className="form-grid compact editor-field-grid">
                    {entity.fields.map((field) => (
                      <EditorFieldInput key={field.name} field={field} value={selectedRecord[field.name]} references={data.references} />
                    ))}
                  </div>
                  <button className="button primary" type="submit">Save record</button>
                </>
              ) : (
                <p className="note">No records yet. Create one below if this area allows manual creation.</p>
              )}
            </form>
            <form className="mini-panel" onSubmit={createRecord} key={`create-${entity.key}`}>
              <h4>Create new</h4>
              {entity.canCreate ? (
                <>
                  <div className="form-grid compact editor-field-grid">
                    {entity.fields.map((field) => (
                      <EditorFieldInput key={field.name} field={field} references={data.references} />
                    ))}
                  </div>
                  <button className="button" type="submit">Create record</button>
                </>
              ) : (
                <p className="note">New {entity.title.toLowerCase()} records use the dedicated invite or workflow form above.</p>
              )}
            </form>
          </div>
        </>
      )}
    </article>
  );
}

function EditorFieldInput({ field, value, references }: { field: AdminEditorField; value?: unknown; references: Record<string, Record<string, unknown>[]> }) {
  const defaultValue = editorInputValue(value, field.type);
  const id = `editor-${field.name}`;
  if (field.type === "boolean") {
    return (
      <label className="check-field editor-check-field">
        <input id={id} name={field.name} value="1" type="checkbox" defaultChecked={Boolean(Number(value ?? 0)) || value === true} />
        {field.label}
      </label>
    );
  }

  if (field.reference) {
    const options = references[field.reference] ?? [];
    return (
      <label>
        {field.label}
        <select name={field.name} defaultValue={defaultValue} required={field.required}>
          <option value="">Unassigned</option>
          {options.map((option) => (
            <option key={String(option.id)} value={String(option.id)}>
              {stringField(option, "label") || editorRecordLabel(option, { idField: "id", title: field.label } as AdminEditorEntity)}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.options?.length) {
    return (
      <label>
        {field.label}
        <select name={field.name} defaultValue={defaultValue} required={field.required}>
          <option value="">Choose</option>
          {field.options.map((option) => (
            <option key={option} value={option}>{humanize(option)}</option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === "textarea" || field.type === "json") {
    return (
      <label className="wide-field">
        {field.label}
        <textarea name={field.name} required={field.required} defaultValue={defaultValue} />
      </label>
    );
  }

  return (
    <label>
      {field.label}
      <input name={field.name} type={field.type === "date" ? "date" : field.type === "number" || field.type === "money" ? "number" : "text"} step={field.type === "money" ? "0.01" : undefined} required={field.required} defaultValue={defaultValue} />
    </label>
  );
}

function collectEditorValues(form: HTMLFormElement, fields: AdminEditorField[]) {
  const formData = new FormData(form);
  const values: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.type === "boolean") {
      values[field.name] = formData.get(field.name) === "1";
    } else {
      values[field.name] = formData.get(field.name);
    }
  }
  return values;
}

function editorInputValue(value: unknown, type?: AdminEditorField["type"]) {
  if (value === null || value === undefined) return "";
  if (type === "json" && typeof value !== "string") return JSON.stringify(value, null, 2);
  return String(value);
}

function editorRecordLabel(row: Record<string, unknown>, entity: Pick<AdminEditorEntity, "idField" | "title">) {
  return (
    stringField(row, "title") ||
    stringField(row, "name") ||
    stringField(row, "email") ||
    stringField(row, "display_name") ||
    stringField(row, "organization") ||
    stringField(row, "campaign_name") ||
    `${entity.title} #${stringField(row, entity.idField)}`
  );
}

function DashboardLists({ data, kind }: { data: Record<string, unknown>; kind: "admin" | "author" }) {
  const sales = arrayOfRecords(data.salesSummary);
  const ads = arrayOfRecords(data.advertisingMetrics);
  const funnels = arrayOfRecords(data.buyerFunnels);
  const pipeline = arrayOfRecords(data.pipeline);
  const recentOrders = arrayOfRecords(data.recentOrders);
  return (
    <div className="portal-two-col">
      <article className="data-panel">
        <h3>
          <BarChart3 size={19} /> Sales and advertising
        </h3>
        <div className="compact-table">
          {(sales.length ? sales : arrayOfRecords(data.salesByBook)).slice(0, 8).map((row, index) => (
            <div key={`${stringField(row, "title")}-${index}`}>
              <span>{stringField(row, "title") || "Untitled"}</span>
              <strong>
                {numberField(row, "units")} units - {money(numberField(row, "gross"))}
              </strong>
            </div>
          ))}
          {ads.slice(0, 6).map((row, index) => (
            <div key={`${stringField(row, "campaign_name")}-${index}`}>
              <span>{stringField(row, "platform")} - {stringField(row, "campaign_name")}</span>
              <strong>
                {numberField(row, "clicks")} clicks - {money(numberField(row, "spend"))} spend
              </strong>
            </div>
          ))}
        </div>
      </article>
      <article className="data-panel">
        <h3>
          <ShoppingBag size={19} /> Buyer funnel
        </h3>
        <div className="funnel-list">
          {funnels.length === 0 && <p className="note">Funnel events appear after imports or tracking integrations are connected.</p>}
          {funnels.slice(0, 10).map((row, index) => (
            <div key={`${stringField(row, "source")}-${stringField(row, "funnel_step")}-${index}`}>
              <span>{humanize(stringField(row, "funnel_step"))}</span>
              <strong>{numberField(row, "event_count")}</strong>
              <small>{stringField(row, "source")} {numberField(row, "revenue") ? ` -  ${money(numberField(row, "revenue"))}` : ""}</small>
            </div>
          ))}
        </div>
      </article>
      <article className="data-panel">
        <h3>
          <ClipboardList size={19} /> Publication work
        </h3>
        <div className="compact-table">
          {pipeline.slice(0, 8).map((row, index) => (
            <div key={`${stringField(row, "id")}-${index}`}>
              <span>{stringField(row, "title")}</span>
              <strong>{stringField(row, "column_key")} {numberField(row, "blocked") ? " -  blocked" : ""}</strong>
            </div>
          ))}
          {pipeline.length === 0 && <p className="note">No active production tasks in this scope.</p>}
        </div>
      </article>
      {kind === "admin" && (
        <article className="data-panel">
          <h3>
            <ReceiptText size={19} /> Recent orders
          </h3>
          <div className="compact-table">
            {recentOrders.map((row, index) => (
              <div key={`${stringField(row, "id")}-${index}`}>
                <span>{stringField(row, "customer_name") || "Customer"}</span>
                <strong>{stringField(row, "channel")} - {money(numberField(row, "gross_amount"))}</strong>
              </div>
            ))}
          </div>
        </article>
      )}
    </div>
  );
}

function CatalogManagementWorkspace() {
  const [catalog, setCatalog] = useState<Omit<CatalogState, "loading" | "source"> | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [selectedAuthorId, setSelectedAuthorId] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [salesCsv, setSalesCsv] = useState("");
  const [salesPreview, setSalesPreview] = useState<Record<string, unknown> | null>(null);

  async function loadCatalog() {
    setError("");
    try {
      const result = await apiGet<Record<string, unknown>>("/api/author/catalog");
      const normalized = normalizeCatalog(result);
      setCatalog(normalized);
      if (!selectedId && normalized.books[0]?.id) setSelectedId(String(normalized.books[0].id));
      if (!selectedAuthorId && normalized.authors[0]?.id) setSelectedAuthorId(String(normalized.authors[0].id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load author catalog");
    }
  }

  useEffect(() => {
    void loadCatalog();
  }, []);

  const books = catalog?.books ?? [];
  const authors = catalog?.authors ?? [];
  const selectedBook = books.find((book) => String(book.id) === selectedId) ?? books[0];
  const selectedAuthor = authors.find((author) => String(author.id) === selectedAuthorId) ?? authors[0];

  async function updateAuthor(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedAuthor?.id) return;
    const form = new FormData(event.currentTarget);
    setStatus("");
    setError("");
    try {
      await apiPatch(`/api/author/authors/${selectedAuthor.id}`, {
        name: form.get("name"),
        short_bio: form.get("shortBio"),
        long_bio: form.get("longBio"),
        photo_url: form.get("photoUrl"),
        public_contact_note: form.get("publicContactNote"),
        interview_topics_json: parseLinesOrJson(String(form.get("interviewTopics") || ""))
      });
      setStatus("Author profile saved to D1.");
      await loadCatalog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save author profile");
    }
  }

  async function updateBook(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedBook?.id) return;
    const form = new FormData(event.currentTarget);
    setStatus("");
    setError("");
    try {
      await apiPatch(`/api/author/books/${selectedBook.id}`, {
        title: form.get("title"),
        subtitle: form.get("subtitle"),
        description: form.get("description"),
        short_hook: form.get("shortHook"),
        reader_promise: form.get("readerPromise"),
        publication_status: form.get("publicationStatus"),
        publication_date: form.get("publicationDate"),
        kdp_metadata_json: {
          keywords: form.get("kdpKeywords"),
          categories: form.get("kdpCategories"),
          notes: form.get("kdpNotes")
        }
      });
      setStatus("Book metadata saved to D1.");
      await loadCatalog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save book metadata");
    }
  }

  async function addEdition(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedBook?.id) return;
    const form = new FormData(event.currentTarget);
    setStatus("");
    setError("");
    try {
      await apiPost(`/api/author/books/${selectedBook.id}/editions`, {
        format: form.get("format"),
        isbn: form.get("isbn"),
        isbnOwner: form.get("isbnOwner"),
        trimSize: form.get("trimSize"),
        pageCount: form.get("pageCount"),
        listPrice: form.get("listPrice"),
        publicationDate: form.get("publicationDate"),
        status: form.get("status"),
        kdpStatus: form.get("kdpStatus"),
        ingramStatus: form.get("ingramStatus")
      });
      setStatus("Edition saved to D1.");
      event.currentTarget.reset();
      await loadCatalog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to add edition");
    }
  }

  async function addIsbn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedBook?.id) return;
    const form = new FormData(event.currentTarget);
    setStatus("");
    setError("");
    try {
      await apiPost(`/api/author/books/${selectedBook.id}/isbns`, {
        isbn: form.get("isbn"),
        format: form.get("format"),
        owner: form.get("owner"),
        status: form.get("status"),
        notes: form.get("notes")
      });
      setStatus("ISBN saved to D1.");
      event.currentTarget.reset();
      await loadCatalog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to add ISBN");
    }
  }

  async function addCoverAsset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedBook?.id) return;
    const form = new FormData(event.currentTarget);
    setStatus("");
    setError("");
    try {
      await apiUpload(`/api/author/books/${selectedBook.id}/assets`, form);
      setStatus("Cover uploaded to media storage and published from D1.");
      event.currentTarget.reset();
      await loadCatalog();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to upload cover asset");
    }
  }

  async function addConnection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setStatus("");
    setError("");
    try {
      await apiPost("/api/author/connections", {
        provider: form.get("provider"),
        accountLabel: form.get("accountLabel"),
        authType: form.get("authType"),
        connectionStatus: form.get("connectionStatus")
      });
      setStatus("Platform connection record saved.");
      event.currentTarget.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to save platform connection");
    }
  }

  async function onSalesFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setSalesCsv(await file.text());
  }

  async function previewSales() {
    setStatus("");
    setError("");
    try {
      const preview = await apiPost<Record<string, unknown>>("/api/author/imports/preview", {
        source: "kdp_report",
        fileName: "author-sales-report.csv",
        content: salesCsv
      });
      setSalesPreview(preview);
      setStatus("Sales report preview ready.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to preview sales report");
    }
  }

  async function commitSales() {
    setStatus("");
    setError("");
    try {
      const result = await apiPost<Record<string, unknown>>("/api/author/imports/commit", {
        source: "kdp_report",
        fileName: "author-sales-report.csv",
        content: salesCsv
      });
      setStatus(`Sales report imported: ${numberField(result, "inserted")} rows, ${numberField(result, "skipped")} skipped.`);
      setSalesPreview(null);
      setSalesCsv("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to import sales report");
    }
  }

  return (
    <article className="data-panel catalog-workspace">
      <div className="panel-heading-row">
        <h3>
          <BookOpen size={19} /> Catalog management
        </h3>
      </div>
      {error && <p className="form-status error">{error}</p>}
      {status && <p className="form-status success">{status}</p>}
      {!catalog && !error && <p className="note">Loading scoped catalog records from D1...</p>}
      {catalog && authors.length > 0 && selectedAuthor && (
        <div className="author-profile-editor">
          <label className="wide-field">
            Active author profile
            <select value={String(selectedAuthor.id)} onChange={(event) => setSelectedAuthorId(event.target.value)}>
              {authors.map((author) => (
                <option key={String(author.id)} value={String(author.id)}>
                  {author.name}
                </option>
              ))}
            </select>
          </label>
          <form className="mini-panel" onSubmit={updateAuthor} key={`author-${selectedAuthor.id}`}>
            <h4>Author public profile</h4>
            <div className="form-grid compact">
              <TextInput name="name" label="Author name" required defaultValue={selectedAuthor.name} />
              <TextInput name="photoUrl" label="Photo URL" defaultValue={selectedAuthor.photo} />
            </div>
            <label>
              Short bio
              <textarea name="shortBio" defaultValue={selectedAuthor.shortBio} />
            </label>
            <label>
              Long bio
              <textarea name="longBio" defaultValue={selectedAuthor.longBio} />
            </label>
            <label>
              Public contact note
              <textarea name="publicContactNote" defaultValue={selectedAuthor.publicContactNote} />
            </label>
            <label>
              Interview topics
              <textarea name="interviewTopics" defaultValue={selectedAuthor.interviewTopics.join("\n")} />
            </label>
            <button className="button primary" type="submit">Save author profile</button>
          </form>
        </div>
      )}
      {catalog && books.length === 0 && <p className="note">No books are assigned to this account in D1.</p>}
      {catalog && books.length > 0 && selectedBook && (
        <>
          <label className="wide-field">
            Active book
            <select value={String(selectedBook.id)} onChange={(event) => setSelectedId(event.target.value)}>
              {books.map((book) => (
                <option key={String(book.id)} value={String(book.id)}>
                  {book.title}
                </option>
              ))}
            </select>
          </label>
          <div className="portal-two-col">
            <form className="mini-panel" onSubmit={updateBook} key={`book-${selectedBook.id}`}>
              <h4>Book metadata</h4>
              <TextInput name="title" label="Title" required defaultValue={selectedBook.title} />
              <TextInput name="subtitle" label="Subtitle" defaultValue={selectedBook.subtitle} />
              <TextInput name="publicationDate" label="Publication date" type="date" defaultValue={selectedBook.publicationDate} />
              <label>
                Publication status
                <select name="publicationStatus" defaultValue={selectedBook.publicationStatus || "manuscript"}>
                  {["manuscript", "editorial", "metadata", "proof", "published", "paused", "archived"].map((item) => (
                    <option key={item} value={item}>{humanize(item)}</option>
                  ))}
                </select>
              </label>
              <label>
                Hook
                <textarea name="shortHook" defaultValue={selectedBook.hook} />
              </label>
              <label>
                Description
                <textarea name="description" defaultValue={selectedBook.description} />
              </label>
              <label>
                Reader promise
                <textarea name="readerPromise" defaultValue={selectedBook.promise} />
              </label>
              <div className="form-grid compact">
                <TextInput name="kdpKeywords" label="KDP keywords" />
                <TextInput name="kdpCategories" label="KDP categories" />
              </div>
              <label>
                KDP notes
                <textarea name="kdpNotes" placeholder="Keyword boxes, category notes, or upload checklist state." />
              </label>
              <button className="button primary" type="submit">Save metadata</button>
            </form>
            <div className="mini-panel">
              <h4>Current records</h4>
              <div className="compact-table">
                <div>
                  <span>Status</span>
                  <strong>{humanize(selectedBook.publicationStatus || "unknown")}</strong>
                </div>
                <div>
                  <span>Formats</span>
                  <strong>{selectedBook.formats.join(", ") || "No editions yet"}</strong>
                </div>
                <div>
                  <span>ISBNs</span>
                  <strong>{Object.values(selectedBook.isbn).join(", ") || "No ISBNs yet"}</strong>
                </div>
                <div>
                  <span>Cover asset</span>
                  <strong>{selectedBook.coverImage ? "Public media asset set" : "No public cover asset"}</strong>
                </div>
              </div>
              {selectedBook.coverImage && (
                <div className="portal-cover-preview">
                  <BookCover title={selectedBook.title} tone={selectedBook.coverTone} order={selectedBook.seriesOrder} image={selectedBook.coverImage} />
                </div>
              )}
              <div className="file-chip-row">
                {selectedBook.assets?.map((asset) => (
                  <span key={String(asset.id)}>
                    <FileUp size={14} />
                    {stringField(asset, "title")} - {humanize(stringField(asset, "asset_type"))}
                  </span>
                ))}
              </div>
            </div>
            <form className="mini-panel" onSubmit={addEdition}>
              <h4>Add edition</h4>
              <div className="form-grid compact">
                <TextInput name="format" label="Format" required defaultValue="Paperback" />
                <TextInput name="isbn" label="ISBN" />
                <TextInput name="isbnOwner" label="ISBN owner" />
                <TextInput name="trimSize" label="Trim size" />
                <TextInput name="pageCount" label="Page count" type="number" />
                <TextInput name="listPrice" label="List price" />
                <TextInput name="publicationDate" label="Publication date" type="date" />
              </div>
              <div className="form-grid compact">
                <TextInput name="status" label="Edition status" defaultValue="planned" />
                <TextInput name="kdpStatus" label="KDP status" />
                <TextInput name="ingramStatus" label="Ingram status" />
              </div>
              <button className="button" type="submit">Add edition</button>
            </form>
            <form className="mini-panel" onSubmit={addIsbn}>
              <h4>Add ISBN</h4>
              <TextInput name="isbn" label="ISBN" required />
              <div className="form-grid compact">
                <TextInput name="format" label="Format" />
                <TextInput name="owner" label="Owner" />
                <TextInput name="status" label="Status" defaultValue="assigned" />
              </div>
              <label>
                Notes
                <textarea name="notes" />
              </label>
              <button className="button" type="submit">Save ISBN</button>
            </form>
            <form className="mini-panel" onSubmit={addCoverAsset}>
              <h4>Replace public cover</h4>
              <TextInput name="title" label="Asset title" defaultValue={`${selectedBook.title} cover`} />
              <div className="form-grid compact">
                <TextInput name="assetType" label="Asset type" defaultValue="cover" />
                <TextInput name="visibility" label="Visibility" defaultValue="public" />
              </div>
              <label>
                Cover image
                <input name="file" required type="file" accept="image/png,image/jpeg,image/webp" />
              </label>
              <p className="note">PNG, JPEG, or WebP. The previous public cover remains in the portal archive.</p>
              <button className="button" type="submit">Upload cover</button>
            </form>
            <form className="mini-panel" onSubmit={addConnection}>
              <h4>Platform connection</h4>
              <label>
                Provider
                <select name="provider" defaultValue="amazon_kdp">
                  {["amazon_kdp", "ingramspark", "apple_books", "kobo_writing_life", "amazon_ads", "meta_ads"].map((provider) => (
                    <option key={provider} value={provider}>{humanize(provider)}</option>
                  ))}
                </select>
              </label>
              <TextInput name="accountLabel" label="Account label" />
              <div className="form-grid compact">
                <TextInput name="authType" label="Auth type" defaultValue="report_import" />
                <TextInput name="connectionStatus" label="Status" defaultValue="needs_report_import" />
              </div>
              <p className="note">KDP sales visibility is powered by report import unless Amazon exposes a supported KDP sales API.</p>
              <button className="button" type="submit">Save connection</button>
            </form>
            <div className="mini-panel">
              <h4>KDP / sales report import</h4>
              <label>
                CSV report
                <input type="file" accept=".csv,text/csv" onChange={onSalesFile} />
              </label>
              <textarea value={salesCsv} onChange={(event) => setSalesCsv(event.target.value)} placeholder="Paste KDP or distributor CSV rows here." />
              <div className="button-row">
                <button className="button" type="button" onClick={previewSales} disabled={!salesCsv.trim()}>Preview</button>
                <button className="button primary" type="button" onClick={commitSales} disabled={!salesCsv.trim()}>Import</button>
              </div>
              {salesPreview && <pre>{JSON.stringify(salesPreview, null, 2)}</pre>}
            </div>
          </div>
        </>
      )}
    </article>
  );
}

function PublicationWorkspace({ data }: { data: Record<string, unknown> }) {
  const channels = arrayOfRecords(data.distributionChannels);
  const packages = arrayOfRecords(data.publicationPackages);
  const jobs = arrayOfRecords(data.publicationJobs);
  const files = arrayOfRecords(data.publicationFiles);
  const authors = arrayOfRecords(data.authors);
  const [status, setStatus] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");

  async function createPackage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("");
    const form = new FormData(event.currentTarget);
    try {
      await apiPost("/api/author/packages", {
        title: form.get("title"),
        subtitle: form.get("subtitle"),
        authorName: form.get("authorName"),
        authorId: form.get("authorId") ? Number(form.get("authorId")) : undefined,
        projectType: form.get("projectType"),
        notes: form.get("notes"),
        rightsConfirmed: form.get("rightsConfirmed") === "on",
        channels: form.getAll("channels")
      });
      setStatus("Publication package opened.");
      event.currentTarget.reset();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Unable to create package");
    }
  }

  async function uploadFile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setUploadStatus("");
    const form = new FormData(event.currentTarget);
    const packageId = String(form.get("packageId") || "");
    if (!packageId) {
      setUploadStatus("Choose a package first.");
      return;
    }
    try {
      await apiUpload(`/api/author/packages/${encodeURIComponent(packageId)}/files`, form);
      setUploadStatus("File received for review.");
      event.currentTarget.reset();
    } catch (err) {
      setUploadStatus(err instanceof Error ? err.message : "Unable to upload file");
    }
  }

  return (
    <article className="data-panel publication-workspace">
      <div className="panel-heading-row">
        <h3>
          <PackageCheck size={19} /> Publishing workspace
        </h3>
      </div>
      <div className="publication-grid">
        <form onSubmit={createPackage}>
          <h4>Open a package</h4>
          <label>
            Title
            <input name="title" required placeholder="Book title" />
          </label>
          <label>
            Subtitle
            <input name="subtitle" placeholder="Optional" />
          </label>
          <div className="form-grid compact">
            <label>
              Author name
              <input name="authorName" required placeholder="Author name" />
            </label>
            <label>
              Project type
              <select name="projectType" defaultValue="book">
                <option value="book">Book</option>
                <option value="series">Series</option>
                <option value="revision">Revision</option>
                <option value="rights_package">Rights package</option>
              </select>
            </label>
          </div>
          {authors.length > 0 && (
            <label>
              Catalog author
              <select name="authorId" defaultValue="">
                <option value="">Unassigned</option>
                {authors.map((author) => (
                  <option key={String(author.id)} value={String(author.id)}>
                    {stringField(author, "name")}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="channel-picker">
            {channels.map((channel) => (
              <label key={String(channel.id)} className="check-field">
                <input type="checkbox" name="channels" value={String(channel.id)} />
                {stringField(channel, "display_name")}
              </label>
            ))}
          </div>
          <label>
            Notes
            <textarea name="notes" placeholder="Files, format goals, launch timing, rights notes." />
          </label>
          <label className="check-field">
            <input name="rightsConfirmed" type="checkbox" required />
            Rights are controlled for this package
          </label>
          <button className="button primary" type="submit">
            Open package
          </button>
          {status && <p className="form-status">{status}</p>}
        </form>
        <form onSubmit={uploadFile}>
          <h4>Upload raw files</h4>
          <label>
            Package
            <select name="packageId" required>
              <option value="">Choose package</option>
              {packages.map((pkg) => (
                <option key={String(pkg.id)} value={String(pkg.id)}>
                  {stringField(pkg, "title")}
                </option>
              ))}
            </select>
          </label>
          <label>
            File role
            <select name="fileRole" defaultValue="manuscript">
              <option value="manuscript">Manuscript</option>
              <option value="cover">Cover</option>
              <option value="interior_pdf">Interior PDF</option>
              <option value="epub">EPUB</option>
              <option value="metadata">Metadata</option>
            </select>
          </label>
          <label>
            File
            <input name="file" type="file" required />
          </label>
          <label>
            Notes
            <textarea name="notes" placeholder="Version, source app, known issues, requested use." />
          </label>
          <button className="button" type="submit">
            <UploadCloud size={17} /> Upload
          </button>
          {uploadStatus && <p className="form-status">{uploadStatus}</p>}
        </form>
      </div>
      <div className="portal-two-col">
        <article className="mini-panel">
          <h4>Packages</h4>
          <div className="compact-table">
            {packages.slice(0, 8).map((pkg) => (
              <div key={String(pkg.id)}>
                <span>{stringField(pkg, "title")}</span>
                <strong>{humanize(stringField(pkg, "status"))} - {numberField(pkg, "file_count")} files - {numberField(pkg, "job_count")} jobs</strong>
              </div>
            ))}
          </div>
        </article>
        <article className="mini-panel">
          <h4>Distribution jobs</h4>
          <div className="compact-table">
            {jobs.slice(0, 8).map((job) => (
              <div key={String(job.id)}>
                <span>{stringField(job, "package_title")} - {stringField(job, "channel")}</span>
                <strong>{humanize(stringField(job, "status"))} - {humanize(stringField(job, "automation_level"))}</strong>
              </div>
            ))}
          </div>
        </article>
        <article className="mini-panel wide">
          <h4>Files</h4>
          <div className="file-chip-row">
            {files.slice(0, 12).map((file) => (
              <span key={String(file.id)}>
                <FileUp size={14} />
                {stringField(file, "file_name")} - {humanize(stringField(file, "validation_status"))}
              </span>
            ))}
            {files.length === 0 && <p className="note">Uploaded files appear here after a package is opened.</p>}
          </div>
        </article>
      </div>
    </article>
  );
}

function MessageWorkspace({ data, kind }: { data: Record<string, unknown>; kind: "admin" | "author" }) {
  const threads = arrayOfRecords(data.messageThreads);
  const packages = arrayOfRecords(data.publicationPackages);
  const [status, setStatus] = useState("");

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("");
    const form = new FormData(event.currentTarget);
    try {
      await apiPost("/api/author/messages", {
        subject: form.get("subject"),
        message: form.get("message"),
        packageId: form.get("packageId")
      });
      setStatus("Message sent.");
      event.currentTarget.reset();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Unable to send message");
    }
  }

  return (
    <article className="data-panel">
      <div className="panel-heading-row">
        <h3>
          <MessageSquare size={19} /> Author and staff messages
        </h3>
      </div>
      <div className="message-workspace-grid">
        <form onSubmit={sendMessage}>
          <label>
            Subject
            <input name="subject" required placeholder="Publication question" />
          </label>
          <label>
            Related package
            <select name="packageId" defaultValue="">
              <option value="">No package</option>
              {packages.map((pkg) => (
                <option key={String(pkg.id)} value={String(pkg.id)}>
                  {stringField(pkg, "title")}
                </option>
              ))}
            </select>
          </label>
          <label>
            Message
            <textarea name="message" required placeholder="Write the message." />
          </label>
          <button className="button primary" type="submit">
            <Send size={17} /> Send
          </button>
          {status && <p className="form-status">{status}</p>}
        </form>
        <div className="thread-list portal-thread-list">
          {threads.length === 0 && <p className="note">Messages between authors and Castlehale staff will appear here.</p>}
          {threads.slice(0, 8).map((thread) => (
            <ThreadCard key={String(thread.id)} thread={thread} kind={kind} />
          ))}
        </div>
      </div>
    </article>
  );
}

function ThreadCard({ thread, kind }: { thread: Record<string, unknown>; kind: "admin" | "author" }) {
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState("");
  async function sendReply() {
    if (!reply.trim()) return;
    setStatus("");
    try {
      const base = kind === "admin" ? "/api/admin/messages" : "/api/author/messages";
      await apiPost(`${base}/${encodeURIComponent(String(thread.id))}/reply`, { message: reply });
      setStatus("Reply sent.");
      setReply("");
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Unable to reply");
    }
  }
  return (
    <details className="message-thread">
      <summary>
        <span>
          <strong>{stringField(thread, "subject")}</strong>
          <small>{humanize(stringField(thread, "thread_type"))} - {humanize(stringField(thread, "status"))}</small>
        </span>
        <ChevronRight size={17} />
      </summary>
      <p>{stringField(thread, "latest_body")}</p>
      <div className="reply-row">
        <input value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Reply" />
        <button className="button" type="button" onClick={sendReply}>
          <Send size={16} />
        </button>
      </div>
      {status && <p className="form-status">{status}</p>}
    </details>
  );
}

function PayPalOrderForm({ books, loading, error }: { books: CatalogBook[]; loading: boolean; error?: string }) {
  const checkoutBooks = books.filter((book) => book.id && Object.keys(book.listPrices ?? {}).length > 0);
  const [selectedBookId, setSelectedBookId] = useState(() => (checkoutBooks[0]?.id ? String(checkoutBooks[0].id) : ""));
  const [quantity, setQuantity] = useState(1);
  const [shippingOption, setShippingOption] = useState("us_media_mail");
  const [status, setStatus] = useState("");
  const [formError, setFormError] = useState("");
  const selectedBook = checkoutBooks.find((book) => String(book.id) === selectedBookId) ?? checkoutBooks[0];
  const unitPrice = selectedBook ? publicBookPrice(selectedBook) : 0;
  const shipping = shippingOption === "us_priority" ? 9.95 : 5.95;
  const estimatedTotal = unitPrice ? unitPrice * quantity + shipping : 0;

  useEffect(() => {
    if (!selectedBookId && checkoutBooks[0]?.id) setSelectedBookId(String(checkoutBooks[0].id));
  }, [checkoutBooks, selectedBookId]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedBook?.id) return;
    setStatus("Opening PayPal checkout...");
    setFormError("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await apiPost<{ approvalUrl: string }>("/api/payments/paypal/create-order", {
        customer: {
          name: form.get("name"),
          email: form.get("email")
        },
        shippingAddress: {
          name: form.get("name"),
          addressLine1: form.get("addressLine1"),
          addressLine2: form.get("addressLine2"),
          city: form.get("city"),
          state: form.get("state"),
          postalCode: form.get("postalCode"),
          countryCode: form.get("countryCode"),
          phone: form.get("phone")
        },
        shippingOption,
        fulfillmentProvider: "lulu_print_api",
        notes: form.get("notes"),
        items: [{ bookId: selectedBook.id, quantity }]
      });
      window.location.href = result.approvalUrl;
    } catch (err) {
      setStatus("");
      setFormError(err instanceof Error ? err.message : "Unable to open PayPal checkout");
    }
  }

  if (loading) return <p className="note">Loading checkout catalog...</p>;
  if (error) return <p className="form-status error">{error}</p>;
  if (checkoutBooks.length === 0) {
    return <p className="note">No priced direct-checkout titles are currently published in the catalog.</p>;
  }

  return (
    <form className="commerce-checkout" onSubmit={onSubmit}>
      <div className="checkout-book-select">
        <label>
          Title
          <select value={selectedBookId} onChange={(event) => setSelectedBookId(event.target.value)} required>
            {checkoutBooks.map((book) => (
              <option key={String(book.id)} value={String(book.id)}>
                {book.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          Quantity
          <input value={quantity} type="number" min={1} max={25} onChange={(event) => setQuantity(Math.max(1, Number(event.target.value) || 1))} />
        </label>
        <label>
          Shipping
          <select value={shippingOption} onChange={(event) => setShippingOption(event.target.value)}>
            <option value="us_media_mail">US Media Mail - $5.95</option>
            <option value="us_priority">US Priority Mail - $9.95</option>
          </select>
        </label>
      </div>
      <div className="checkout-summary">
        {selectedBook && (
          <div className="checkout-cover">
            <BookCover title={selectedBook.title} tone={selectedBook.coverTone} order={selectedBook.seriesOrder} image={selectedBook.coverImage} />
          </div>
        )}
        <div>
          <span>{selectedBook?.title}</span>
          <strong>{unitPrice ? moneyWithCents(estimatedTotal) : "Price unavailable"}</strong>
          <small>{unitPrice ? `${quantity} x ${moneyWithCents(unitPrice)} plus shipping` : "Select another title or send a request below."}</small>
        </div>
      </div>
      <div className="form-grid">
        <TextInput name="name" label="Name" required />
        <TextInput name="email" label="Email" type="email" required />
        <TextInput name="phone" label="Phone" />
        <TextInput name="countryCode" label="Country code" required defaultValue="US" />
        <TextInput name="addressLine1" label="Address line 1" required />
        <TextInput name="addressLine2" label="Address line 2" />
        <TextInput name="city" label="City" required />
        <TextInput name="state" label="State" required />
        <TextInput name="postalCode" label="Postal code" required />
      </div>
      <label>
        Order notes
        <textarea name="notes" placeholder="Gift note, deadline, or delivery details." />
      </label>
      <button className="button primary" type="submit" disabled={!unitPrice}>
        Checkout with PayPal
      </button>
      {status && <p className="form-status">{status}</p>}
      {formError && <p className="form-status error">{formError}</p>}
    </form>
  );
}

function ReaderLeadForm({ books }: { books: CatalogBook[] }) {
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("");
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await apiPost("/api/public/reader-leads", {
        name: form.get("name"),
        email: form.get("email"),
        bookInterest: form.get("bookInterest"),
        requestType: form.get("requestType"),
        notes: form.get("notes")
      });
      setStatus("Request received. Castlehale will follow up through the contact information provided.");
      event.currentTarget.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send request");
    }
  }

  return (
    <form className="inquiry-form compact-lead-form" onSubmit={onSubmit}>
      <div className="form-grid">
        <TextInput name="name" label="Name" required />
        <TextInput name="email" label="Email" type="email" required />
        <label>
          Book / title interest
          <select name="bookInterest" required>
            {books.map((book) => (
              <option value={book.title} key={book.slug}>
                {book.title}
              </option>
            ))}
            <option value="General Castlehale order">General Castlehale order</option>
          </select>
        </label>
        <SelectInput name="requestType" label="Request type" options={["Signed copy", "Reader bundle", "Gift order", "Event stock", "Book club order", "Availability question"]} />
      </div>
      <label>
        Notes
        <textarea name="notes" placeholder="Quantity, shipping region, deadline, gift note, event details, or format preference." />
      </label>
      <button className="button primary" type="submit">
        Send order request
      </button>
      {error && <p className="form-status error">{error}</p>}
      {status && <p className="form-status success">{status}</p>}
    </form>
  );
}

function ConnectionPanel({ data, kind }: { data: Record<string, unknown>; kind: "admin" | "author" }) {
  const channels = arrayOfRecords(data.distributionChannels);
  const connections = arrayOfRecords(data.platformConnections);
  return (
    <article className="data-panel">
      <div className="panel-heading-row">
        <h3>
          <PlugZap size={19} /> Channels and integrations
        </h3>
      </div>
      <div className="channel-status-grid">
        {channels.map((channel) => (
          <div key={String(channel.id)} className="channel-status">
            <strong>{stringField(channel, "display_name")}</strong>
            <span>{humanize(stringField(channel, "channel_type"))}</span>
            <small>{humanize(stringField(channel, "automation_level"))} - {humanize(stringField(channel, "status"))}</small>
          </div>
        ))}
      </div>
      <div className="compact-table connections-table">
        {connections.slice(0, 8).map((connection) => (
          <div key={String(connection.id)}>
            <span>{humanize(stringField(connection, "provider"))} - {stringField(connection, "account_label")}</span>
            <strong>{humanize(stringField(connection, "connection_status"))}</strong>
          </div>
        ))}
        {connections.length === 0 && <p className="note">{kind === "admin" ? "Register platform accounts above as they are approved." : "Connected platform accounts will appear here."}</p>}
      </div>
    </article>
  );
}

type AdminNotification = {
  id: string;
  title: string;
  body?: string;
  read_at?: string | null;
  created_at?: string;
};

type AdminThread = {
  id: string;
  subject: string;
  status: string;
  related_lead_id?: number;
  related_library_lead_id?: string;
  created_by_name?: string;
  created_by_email?: string;
  genre?: string;
  timeline?: string;
  budget_range?: string;
  library_organization?: string;
  library_request_type?: string;
  library_acquisition_timeline?: string;
  latest_body?: string;
  latest_at?: string;
};

function AdminInbox() {
  const [inbox, setInbox] = useState<{ notifications: AdminNotification[]; threads: AdminThread[] } | null>(null);
  const [error, setError] = useState("");

  async function loadInbox() {
    setError("");
    try {
      setInbox(await apiGet<{ notifications: AdminNotification[]; threads: AdminThread[] }>("/api/admin/messages"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load messages");
    }
  }

  useEffect(() => {
    void loadInbox();
  }, []);

  async function markAllRead() {
    try {
      await apiPost("/api/admin/notifications/read", {});
      await loadInbox();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to update notifications");
    }
  }

  const unread = inbox?.notifications.filter((item) => !item.read_at).length ?? 0;

  return (
    <article className="data-panel message-inbox">
      <div className="panel-heading-row">
        <h3>
          <Mail size={19} /> Messages and notifications
        </h3>
        <button className="button" type="button" onClick={markAllRead} disabled={!unread}>
          Mark read
        </button>
      </div>
      {error && <p className="form-status error">{error}</p>}
      {!inbox && !error && <p className="note">Loading messages...</p>}
      {inbox && (
        <>
          <div className="notification-list">
            {inbox.notifications.length === 0 ? (
              <p className="note">No notifications yet.</p>
            ) : (
              inbox.notifications.slice(0, 5).map((item) => (
                <div className={item.read_at ? "notification" : "notification unread"} key={item.id}>
                  <strong>{item.title}</strong>
                  {item.body && <span>{item.body}</span>}
                </div>
              ))
            )}
          </div>
          <div className="thread-list">
            {inbox.threads.length === 0 ? (
              <p className="note">New project requests will appear here.</p>
            ) : (
              inbox.threads.map((thread) => (
                <details className="message-thread" key={thread.id}>
                  <summary>
                    <span>
                      <strong>{thread.subject}</strong>
                      <small>
                        {thread.library_organization || thread.created_by_email}
                        {thread.library_request_type ? ` - ${thread.library_request_type}` : ""}
                        {thread.library_acquisition_timeline ? ` - ${thread.library_acquisition_timeline}` : ""}
                        {thread.timeline ? ` - ${thread.timeline}` : ""}
                        {thread.budget_range ? ` - ${thread.budget_range}` : ""}
                      </small>
                    </span>
                    <ChevronRight size={17} />
                  </summary>
                  <pre>{thread.latest_body}</pre>
                </details>
              ))
            )}
          </div>
        </>
      )}
    </article>
  );
}

function ImportPreview() {
  const [csv, setCsv] = useState("title,isbn,channel,date,quantity,gross,currency\nSample Title,978-1-000000-00-0,KDP,2026-10-07,3,17.97,USD");
  const [fileName, setFileName] = useState("browser-preview.csv");
  const [preview, setPreview] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState("");
  async function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setError("");
    setFileName(file.name);
    try {
      if (file.name.toLowerCase().endsWith(".xlsx")) {
        const { readSheet } = await import("read-excel-file/browser");
        const rows = await readSheet(file);
        setCsv(rows.map((row) => row.map((cell) => escapeCsvCell(cell == null ? "" : String(cell))).join(",")).join("\n"));
      } else {
        setCsv(await file.text());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to read file");
    }
  }
  async function previewImport() {
    setError("");
    try {
      setPreview(await apiPost("/api/admin/imports/preview", { source: "browser_upload", fileName, content: csv }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to preview import");
    }
  }
  return (
    <article className="data-panel">
      <h3>
        <FileSpreadsheet size={19} /> Sales CSV/XLSX preview
      </h3>
      <label>
        Upload CSV or XLSX
        <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={onFile} />
      </label>
      <textarea value={csv} onChange={(event) => setCsv(event.target.value)} />
      <button className="button primary" onClick={previewImport}>
        Preview rows
      </button>
      {error && <p className="form-status error">{error}</p>}
      {preview && <pre>{JSON.stringify(preview, null, 2)}</pre>}
    </article>
  );
}

function LibraryLeadForm() {
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("");
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      await apiPost("/api/public/library-leads", {
        organization: form.get("organization"),
        contactName: form.get("contactName"),
        email: form.get("email"),
        phone: form.get("phone"),
        country: form.get("country"),
        requestType: form.get("requestType"),
        titleInterest: form.get("titleInterest"),
        acquisitionTimeline: form.get("acquisitionTimeline"),
        notes: form.get("notes")
      });
      setStatus("Library request received. Castlehale will follow up through the contact information provided.");
      event.currentTarget.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to send library request");
    }
  }

  return (
    <form className="inquiry-form compact-lead-form" onSubmit={onSubmit}>
      <div className="form-grid">
        <TextInput name="organization" label="Library / institution" required />
        <TextInput name="contactName" label="Contact name" required />
        <TextInput name="email" label="Work email" type="email" required />
        <TextInput name="phone" label="Phone" />
        <TextInput name="country" label="Country / region" />
        <SelectInput name="requestType" label="Request type" options={["Ordering information", "Collection-development question", "Review copy / examination copy", "Series or author information", "Rights / permissions", "Other"]} />
        <TextInput name="titleInterest" label="Title / author interest" />
        <SelectInput name="acquisitionTimeline" label="Acquisition timeline" options={["Immediate", "This quarter", "This fiscal year", "Future consideration", "Research only"]} />
      </div>
      <label>
        Notes
        <textarea name="notes" placeholder="Collection area, format requirements, ordering system, distributor preference, or follow-up details." />
      </label>
      <button className="button primary" type="submit">
        Send library request
      </button>
      {error && <p className="form-status error">{error}</p>}
      {status && <p className="form-status success">{status}</p>}
    </form>
  );
}

function escapeCsvCell(value: string) {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function InquiryForm() {
  const [status, setStatus] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const data = Object.fromEntries(form.entries());
    const parsed = inquirySchema.safeParse({
      ...data,
      rightsConfirmed: form.get("rightsConfirmed") === "on",
      wordCount: data.wordCount ? Number(data.wordCount) : undefined,
      bookCount: data.bookCount ? Number(data.bookCount) : 1
    });
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((issue) => issue.message));
      setStatus("");
      return;
    }
    setErrors([]);
    try {
      await apiPost("/api/public/inquiries", parsed.data);
      setStatus("Inquiry received. Castlehale will review fit, scope, rights, timeline, and next steps.");
      event.currentTarget.reset();
    } catch (err) {
      setStatus("");
      setErrors([err instanceof Error ? err.message : "Unable to submit inquiry"]);
    }
  }

  return (
    <section className="section form-section">
      <form className="inquiry-form" onSubmit={onSubmit}>
        <div className="form-intro">
          <p className="eyebrow">Publishing services inquiry</p>
          <h2>Request a Castlehale publishing consult.</h2>
          <p>
            This sends a lead to the publisher. Do not send manuscripts or materials here; Castlehale requests files only after accepting a contracted relationship.
          </p>
        </div>
        <div className="form-grid">
          <TextInput name="authorName" label="Author name" required />
          <TextInput name="email" label="Email" type="email" required />
          <SelectInput name="genre" label="Genre" options={inquiryChoices.genre} />
          <SelectInput name="manuscriptStatus" label="Project status" options={inquiryChoices.manuscriptStatus} />
          <TextInput name="wordCount" label="Word count" type="number" />
          <TextInput name="bookCount" label="Number of books" type="number" defaultValue="1" />
          <SelectInput name="projectShape" label="Series or standalone" options={["series", "standalone", "catalog"]} />
          <SelectInput name="editingStatus" label="Editing status" options={inquiryChoices.editingStatus} />
          <SelectInput name="coverStatus" label="Cover status" options={inquiryChoices.coverStatus} />
          <SelectInput name="isbnStatus" label="ISBN status" options={inquiryChoices.isbnStatus} />
          <SelectInput name="timeline" label="Timeline" options={inquiryChoices.timeline} />
          <SelectInput name="budgetRange" label="Budget range" options={inquiryChoices.budgetRange} />
        </div>
        <label>
          Desired services
          <textarea name="desiredServices" required placeholder="Production, cover wrap, edition planning, retailer copy, direct sales, library outreach, launch support, author reporting..." />
        </label>
        <label>
          Notes
          <textarea name="notes" placeholder="Tell us what matters: genre, series plan, release goals, rights questions, scope, timeline, and commercial goals. Do not paste manuscript text." />
        </label>
        <label className="check-field">
          <input type="checkbox" name="rightsConfirmed" required />
          I confirm I own or control the rights needed to inquire about this project.
        </label>
        <button className="button primary" type="submit">
          Send inquiry to publisher
        </button>
        {errors.length > 0 && (
          <div className="form-status error">
            {errors.map((error) => (
              <p key={error}>{error}</p>
            ))}
          </div>
        )}
        {status && <p className="form-status success">{status}</p>}
      </form>
    </section>
  );
}

function PageHero({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return (
    <section className="page-hero">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{copy}</p>
    </section>
  );
}

function BookCard({ book, compact = false }: { book: CatalogBook; compact?: boolean }) {
  return (
    <Link className={compact ? "book-card compact" : "book-card"} to={`/books/${book.slug}`}>
      <BookCover title={book.title} tone={book.coverTone} order={book.seriesOrder} image={book.coverImage} />
      <div>
        <small>{book.subtitle}</small>
        <h3>{book.title}</h3>
        {!compact && <p>{book.hook}</p>}
        <span>{book.authorName}</span>
      </div>
    </Link>
  );
}

function BookCover({ title, tone, order, image, large = false }: { title: string; tone: string; order: number; image?: string; large?: boolean }) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(image && !imageFailed);
  return (
    <div className={`book-cover tone-${tone} ${large ? "large" : ""}`} aria-label={`${title} cover treatment`}>
      {showImage ? (
        <img src={image} alt="" onError={() => setImageFailed(true)} />
      ) : (
        <>
          <span>Castlehale Press</span>
          <strong>{title}</strong>
          <small>Book {order}</small>
        </>
      )}
    </div>
  );
}

function AuthorCard({ author }: { author: CatalogAuthor }) {
  return (
    <Link className="author-card" to={`/authors/${author.slug}`}>
      <img src={author.photo} alt="" />
      <div>
        <small>{author.role}</small>
        <h3>{author.name}</h3>
        <p>{author.shortBio}</p>
      </div>
    </Link>
  );
}

function PackageCard({ pkg }: { pkg: (typeof publishingPackages)[number] }) {
  return (
    <article className="package-card">
      <p className="eyebrow">{pkg.audience}</p>
      <h3>{pkg.name}</h3>
      <strong className="package-price">{pkg.price}</strong>
      <p>{pkg.summary}</p>
      <ul>
        {pkg.includes.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <p className="note">{pkg.bestFor}</p>
    </article>
  );
}

function ChecklistSection({ title, items }: { title: string; items: string[] }) {
  return (
    <Section title={title}>
      <ul className="check-grid">
        {items.map((item) => (
          <li key={item}>
            <CheckCircle2 size={18} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function TextInput({ name, label, type = "text", required = false, defaultValue }: { name: string; label: string; type?: string; required?: boolean; defaultValue?: string }) {
  return (
    <label>
      {label}
      <input name={name} type={type} required={required} defaultValue={defaultValue} />
    </label>
  );
}

function SelectInput({ name, label, options }: { name: string; label: string; options: string[] }) {
  return (
    <label>
      {label}
      <select name={name} required>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function Meta({ label, value, href }: { label: string; value: string; href?: string }) {
  return (
    <div>
      <small>{label}</small>
      {href ? <Link to={href}>{value}</Link> : <strong>{value}</strong>}
    </div>
  );
}

function NotFound({ title }: { title: string }) {
  return <PageHero eyebrow="Not found" title={title} copy="That page is not in the Castlehale catalog yet." />;
}

function arrayOfRecords(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((item): item is Record<string, unknown> => typeof item === "object" && item !== null && !Array.isArray(item)) : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringField(row: Record<string, unknown>, key: string) {
  const value = row[key];
  return value === null || value === undefined ? "" : String(value);
}

function numberField(row: Record<string, unknown>, key: string) {
  const value = Number(row[key] ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function numberFrom(value: unknown) {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function arrayOfStrings(value: unknown) {
  return Array.isArray(value) ? value.map((item) => String(item)).filter(Boolean) : [];
}

function publicBookPrice(book: CatalogBook) {
  const firstPrice = Object.values(book.listPrices ?? {}).find(Boolean);
  if (!firstPrice) return 0;
  const parsed = Number(String(firstPrice).replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseLinesOrJson(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (Array.isArray(parsed)) return parsed.map((item) => String(item)).filter(Boolean);
    } catch {
      return trimmed.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
    }
  }
  return trimmed.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
}

function money(value: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0);
}

function moneyWithCents(value: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);
}

function humanize(key: string) {
  return key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").replace(/^./, (letter) => letter.toUpperCase());
}

function statusClass(value: string) {
  const normalized = value.toLowerCase();
  if (["paid", "captured", "completed", "submitted", "connected", "published", "active"].some((item) => normalized.includes(item))) return "good";
  if (["blocked", "failed", "error", "cancelled", "disabled"].some((item) => normalized.includes(item))) return "bad";
  if (["pending", "awaiting", "queued", "review", "draft", "created"].some((item) => normalized.includes(item))) return "watch";
  return "neutral";
}

export default App;
